

import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import { HiMenu, HiArrowLeft, HiPencil, HiTrash, HiPlay, HiPause, HiArrowsExpand, HiX, HiChevronLeft, HiChevronRight, HiUpload, HiChevronDown, HiEye, HiInformationCircle } from 'react-icons/hi';

import { createLessonContent, updateLessonContent, deleteLessonContent, fetchLessonContentList } from '../../services/lessonContentService';
import { uploadQuizCsv } from '../../services/quizService';
import UploadCsvIcon from '../../assets/icons/upload-csv.svg';
import mantraIcon from '../../assets/icons/ic_mantra.png';
import { uploadImage, uploadFile as uploadAssetFile } from '../../services/assetService';
import Toast from '../../components/toaster';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { lessonContentCache } from './lessonContentCache';
import { bumpUnitLessonsCount } from '../lesson/lessonCache';

// Expandable Text Component
const ExpandableText = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const [exceeds, setExceeds] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (!textRef.current) return;

    const checkHeight = () => {
      const element = textRef.current;
      if (!element) return;
      const computedStyle = window.getComputedStyle(element);
      const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.25;
      const scrollHeight = element.scrollHeight;
      const twoLineHeight = lineHeight * 2;

      // Add a small buffer (2px) to account for rounding
      setExceeds(scrollHeight > twoLineHeight + 2);
    };

    // Use ResizeObserver for robust measurement
    const observer = new ResizeObserver(checkHeight);
    observer.observe(textRef.current);

    // Initial check
    checkHeight();

    return () => observer.disconnect();
  }, [text]);

  if (!text) return null;

  return (
    <div className="mt-3 w-full relative">
      {/* Hidden measurement element */}
      <p
        ref={textRef}
        className="text-xs text-gray-600 leading-5 w-full"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          visibility: 'hidden',
          height: 'auto',
          width: '100%',
          wordBreak: 'normal',
          overflowWrap: 'break-word',
          pointerEvents: 'none',
          zIndex: -1
        }}
      >
        {text}
      </p>

      <p
        className="text-xs text-gray-600"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          wordBreak: "break-word"
        }}
      >
        {text}
      </p>
    </div>
  );
};

const HTTP_URL_RE = /^https?:\/\/.+/i;

/** Coming Soon / Sessions–style URL ↔ file toggle. Mode switch does not clear the saved URL. */
const UrlFileToggleField = ({
  id,
  label,
  value,
  error,
  onChange,
  onClear,
  onUploadingChange,
  disabled = false,
  resetKey = 0,
  accept,
  uploadFn,
  uploadingLabel,
  urlHint,
  fileHint,
  switchToFileHint,
  switchToUrlHint,
  preview = 'none',
  imagePreviewClass = 'h-20 w-20 object-cover rounded-full border border-gray-200 bg-gray-50',
}) => {
  const [source, setSource] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);
  const fileInputRef = useRef(null);
  const trimmed = String(value || '').trim();
  const canPreview = Boolean(trimmed) && HTTP_URL_RE.test(trimmed) && !error && !uploadError;

  useEffect(() => {
    setSource('url');
    setUploading(false);
    setUploadError('');
    setPreviewFailed(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [resetKey]);

  useEffect(() => {
    setPreviewFailed(false);
  }, [trimmed]);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  const switchSource = (next) => {
    if (uploading || disabled) return;
    setSource(next);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file || uploading || disabled) return;
    setUploading(true);
    setUploadError('');
    try {
      onChange(await uploadFn(file));
    } catch (err) {
      setUploadError(err.message || 'Failed to upload. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    if (uploading || disabled) return;
    setUploadError('');
    setPreviewFailed(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClear();
  };

  const showClear = Boolean(trimmed) || Boolean(uploadError);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={id} className="block text-xs font-medium text-gray-700">
          {label}
        </label>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 group relative">
            <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
            <span
              className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[60] sm:whitespace-nowrap leading-tight inline-block ${
                source === 'url' ? 'w-[185px] sm:w-fit sm:max-w-[290px]' : 'w-[175px] sm:w-fit sm:max-w-[240px]'
              }`}
            >
              {source === 'url' ? switchToFileHint : switchToUrlHint}
            </span>
          </div>
          <button
            type="button"
            aria-label={source === 'url' ? 'Switch to file upload' : 'Switch to URL'}
            disabled={uploading || disabled}
            onClick={() => switchSource(source === 'url' ? 'file' : 'url')}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
              source === 'url' ? 'bg-brand' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                source === 'url' ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      {source === 'url' ? (
        <div>
          <div className="flex gap-2">
            <input
              id={id}
              type="url"
              value={value || ''}
              disabled={uploading || disabled}
              onChange={(event) => {
                setUploadError('');
                onChange(event.target.value);
              }}
              placeholder="https://…"
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 ${
                error || uploadError ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'
              }`}
            />
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-400">{urlHint}</p>
        </div>
      ) : (
        <div>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              id={`${id}-file`}
              type="file"
              accept={accept}
              disabled={uploading || disabled}
              onChange={handleFileSelect}
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                uploadError || error ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          {uploading && (
            <div className="mt-2 flex items-center gap-2" role="status" aria-live="polite">
              <svg className="animate-spin h-4 w-4 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-xs text-gray-600">{uploadingLabel}</span>
            </div>
          )}
          <p className="mt-1 text-xs text-gray-400">{fileHint}</p>
        </div>
      )}

      {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
      {error && !uploadError && <p className="mt-1 text-xs text-red-600">{error}</p>}

      {preview === 'image' && canPreview && !previewFailed && !uploading && (
        <div className="mt-2">
          <img
            src={trimmed}
            alt=""
            className={imagePreviewClass}
            onError={() => setPreviewFailed(true)}
          />
        </div>
      )}
      {preview === 'image' && canPreview && previewFailed && !uploading && (
        <p className="mt-2 text-xs text-amber-700">Preview unavailable — URL may be broken.</p>
      )}
      {preview === 'audio' && canPreview && !uploading && (
        <div className="mt-2">
          <audio key={trimmed} src={trimmed} controls preload="metadata" className="w-full h-10" />
        </div>
      )}
      {preview === 'video' && canPreview && !uploading && (
        <div className="mt-2 rounded-lg border border-gray-200 overflow-hidden bg-black">
          <video key={trimmed} src={trimmed} controls className="w-full max-h-48 object-contain" />
        </div>
      )}
    </div>
  );
};

const VIEW_ITEM_TYPE_OPTIONS = [
  { value: 'custom', label: 'Custom' },
  { value: 'html', label: 'HTML' },
];

const getViewItemTypeLabel = (value) =>
  VIEW_ITEM_TYPE_OPTIONS.find((opt) => opt.value === value)?.label || 'Custom';

const normalizeViewItemType = (type) => (type === 'custom' ? 'custom' : 'html');

/** Keep only a single integer for text_size input (digits only). */
const sanitizeTextSizeInput = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '';
  const n = parseInt(digits, 10);
  return Number.isNaN(n) ? '' : String(n);
};

/** Normalize API values like "20px" / 20 → "20" for the form. */
const parseTextSizeFromApi = (value) => {
  if (value == null || value === '') return '';
  const match = String(value).match(/\d+/);
  if (!match) return '';
  const n = parseInt(match[0], 10);
  return Number.isNaN(n) ? '' : String(n);
};

const isValidTextSizeInt = (value) => {
  if (value === '' || value == null) return true; // optional
  return /^\d+$/.test(String(value)) && Number(value) >= 1;
};

const formatTextSizeForCss = (value, fallback = '16') => {
  const n = parseTextSizeFromApi(value) || fallback;
  return `${n}px`;
};

const isViewItemPreviewable = (item) => {
  if (!item) return false;
  if (normalizeViewItemType(item.type) === 'custom') {
    return Boolean(
      String(item.image || '').trim() ||
      item.imageFile ||
      String(item.imagePreview || '').trim() ||
      String(item.text || '').trim()
    );
  }
  return Boolean(String(item.content || '').trim());
};

const getViewItemPeekText = (item) => {
  if (!item) return '';
  if (normalizeViewItemType(item.type) === 'custom') {
    return String(item.text || '').trim() || 'Custom card';
  }
  const content = String(item.content || '').trim();
  if (!content) return 'HTML block';
  return content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() || 'HTML block';
};

const SortableViewItem = ({
  item,
  index,
  onChange,
  onTypeChange,
  onRemove,
  onPreview,
  onImageSelect,
  onImageClear,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item._id,
  });

  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const itemType = normalizeViewItemType(item.type);
  const canPreview = isViewItemPreviewable(item);
  const displayImage = item.imagePreview || item.image || '';
  const hasPendingImageFile = Boolean(item.imageFile);

  const handleTypeSelect = (nextType) => {
    const normalized = normalizeViewItemType(nextType);
    if (onTypeChange) {
      onTypeChange(index, normalized);
    } else {
      onChange(index, { type: normalized });
    }
    setTypeDropdownOpen(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="bg-white border border-gray-200 rounded-lg p-3 space-y-2"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none p-1 flex-shrink-0"
          title="Drag to reorder"
        >
          <HiMenu className="w-4 h-4" />
        </button>
        <span className="text-xs text-gray-500 w-6 text-right flex-shrink-0">{index + 1}.</span>
        <div className="relative flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setTypeDropdownOpen((v) => !v)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent flex items-center justify-between text-left bg-white"
          >
            <span className="truncate">{getViewItemTypeLabel(itemType)}</span>
            <HiChevronDown
              className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ease-in-out ${
                typeDropdownOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
          {typeDropdownOpen && (
            <div className="absolute z-[60] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden py-1">
              {VIEW_ITEM_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleTypeSelect(opt.value)}
                  className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${
                    itemType === opt.value
                      ? 'bg-gray-100 text-black font-semibold'
                      : 'text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onPreview && onPreview(index)}
          disabled={!canPreview}
          className="w-8 h-8 rounded-lg bg-[#EFEBF3] text-brand flex items-center justify-center hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          title={itemType === 'custom' ? 'Preview custom card' : 'Preview HTML'}
        >
          <HiEye className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="w-8 h-8 rounded-lg bg-[#FFF0F0] text-[#F76464] flex items-center justify-center hover:opacity-90 flex-shrink-0"
          title="Remove item"
        >
          <HiTrash className="w-4 h-4" />
        </button>
      </div>

      {itemType === 'html' ? (
        <textarea
          value={item.content || ''}
          onChange={(e) => onChange(index, { content: e.target.value })}
          placeholder="Enter HTML content"
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-y min-h-[72px] max-h-48 font-mono"
        />
      ) : (
        <div className="space-y-2 pl-8">
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Image</label>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand bg-[#EFEBF3] rounded-lg cursor-pointer hover:opacity-90">
                <HiUpload className="w-3.5 h-3.5" />
                Choose image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && onImageSelect) onImageSelect(index, file);
                    e.target.value = '';
                  }}
                />
              </label>
              {(displayImage || hasPendingImageFile) ? (
                <button
                  type="button"
                  onClick={() => onImageClear && onImageClear(index)}
                  className="text-[11px] text-red-500 hover:underline"
                >
                  Clear
                </button>
              ) : null}
              {hasPendingImageFile ? (
                <span className="text-[11px] text-gray-500 truncate max-w-[160px]">
                  {item.imageFile?.name} (uploads on save)
                </span>
              ) : null}
            </div>
            {displayImage ? (
              <div className="mt-2 flex items-start gap-2">
                <img
                  src={displayImage}
                  alt="View item"
                  className="w-16 h-16 object-cover rounded-lg border border-gray-200 bg-gray-50"
                />
                {!hasPendingImageFile ? (
                  <input
                    type="text"
                    value={item.image || ''}
                    onChange={(e) =>
                      onChange(index, {
                        image: e.target.value,
                        imageFile: null,
                        imagePreview: '',
                      })
                    }
                    placeholder="Image URL"
                    className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                  />
                ) : null}
              </div>
            ) : (
              <input
                type="text"
                value={item.image || ''}
                onChange={(e) =>
                  onChange(index, {
                    image: e.target.value,
                    imageFile: null,
                    imagePreview: '',
                  })
                }
                placeholder="Or paste image URL"
                className="mt-2 w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
              />
            )}
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Text</label>
            <textarea
              value={item.text || ''}
              onChange={(e) => onChange(index, { text: e.target.value })}
              placeholder="Card text"
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-y min-h-[56px] max-h-40"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">
              Text size <span className="font-normal text-gray-400">(px, integer)</span>
            </label>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={item.text_size || ''}
              onChange={(e) => onChange(index, { text_size: sanitizeTextSizeInput(e.target.value) })}
              onKeyDown={(e) => {
                // Block decimal / scientific notation keys
                if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              placeholder="e.g. 16"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const VIEW_TYPE_OPTIONS = [
  { value: 'normal_view', label: 'Normal View' },
  { value: 'highlights_view', label: 'Highlights' },
  { value: 'cards_view', label: 'Cards' },
  { value: 'mantra', label: 'Mantra' },
];

const getViewTypeLabel = (value) =>
  VIEW_TYPE_OPTIONS.find((opt) => opt.value === value)?.label || 'Normal View';

const VIEW_ITEMS_PEEK_LIMIT = 3;

const buildPaginationRange = (currentPage, totalPages, maxVisible = 5) => {
  if (totalPages === 0) return [];

  const totalBlocks = maxVisible + 2;

  if (totalPages <= totalBlocks) {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - 1, 1);
  const rightSiblingIndex = Math.min(currentPage + 1, totalPages);
  const shouldShowLeftDots = leftSiblingIndex > 2;
  const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

  const pages = [1];

  const startPage = shouldShowLeftDots ? leftSiblingIndex : 2;
  const endPage = shouldShowRightDots ? rightSiblingIndex : totalPages - 1;

  if (shouldShowLeftDots) pages.push('dots');

  for (let pageNum = startPage; pageNum <= endPage; pageNum += 1) {
    pages.push(pageNum);
  }

  if (shouldShowRightDots) pages.push('dots');

  pages.push(totalPages);
  return pages;
};


const LessonContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { moduleId, chapterId, lessonId } = useParams();
  const fromName = location.state?.fromName; // Unit name (previous page card)
  const moduleName = location.state?.moduleName;
  const chapterName = location.state?.chapterName;
  const unitName = location.state?.unitName || fromName;

  // Helper function to validate MongoDB ObjectId
  const isValidObjectId = (id) => {
    if (!id || typeof id !== 'string') return false;
    const trimmedId = id.trim();
    // MongoDB ObjectId is a 24 character hex string
    return /^[0-9a-fA-F]{24}$/.test(trimmedId);
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(lessonContentCache.page || 1);
  const [limit] = useState(15);
  const [totalPages, setTotalPages] = useState(lessonContentCache.totalPages || 1);
  const [totalCount, setTotalCount] = useState(lessonContentCache.totalCount || 0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(() => {
    return (
      lessonContentCache.lessons !== null &&
      lessonContentCache.lessonId === lessonId &&
      lessonContentCache.page === (lessonContentCache.page || 1)
    );
  });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderLessonContents, setReorderLessonContents] = useState([]);
  const [reorderSaveLoading, setReorderSaveLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadResultModal, setShowUploadResultModal] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [uploadResult, setUploadResult] = useState({
    successful: [],
    skipped: [],
    error: [],
  });

  const [lessons, setLessons] = useState(
    lessonContentCache.lessons !== null && lessonContentCache.lessonId === lessonId
      ? (lessonContentCache.lessons || [])
      : []
  );

  // Unified form state for both Add and Edit
  const [formData, setFormData] = useState({
    id: null, // Only set when editing
    originalType: null, // Original type when editing
    title: '',
    subtitle: '',
    description: '',
    points: '0',
    /** UI-only: uploaded image URL for preview/copy; never sent on save */
    imagePreviewUrl: '',
    /** UI-only: uploaded video URL for preview/copy; never sent on save */
    videoPreviewUrl: '',
    audioFile: null,
    audioUploadedUrl: '',
    audioText: '',
    audioImage: '',
    audioDescription: '',
    order: 1,
    view_type: 'normal_view',
    view_items: [],
    // Mantra-specific fields
    translation: '',
    meaning: '',
    pronunciation: '',
    quick_tip: '',
    practice_guidance: '',
  });

  const [mediaResetKey, setMediaResetKey] = useState(0);
  const [viewTypeDropdownOpen, setViewTypeDropdownOpen] = useState(false);

  const [uploading, setUploading] = useState({
    image: false,
    video: false,
    audio: false,
    audioImage: false,
  });

  const [uploadErrors, setUploadErrors] = useState({
    image: '',
    video: '',
    audio: '',
    audioImage: '',
  });

  const [formErrors, setFormErrors] = useState({
    title: '',
    description: '',
    points: '',
    audio: '',
    order: '',
    view_items: '',
    translation: '',
  });


  //   const isValidUrl = (url) => {
  //   try {
  //     new URL(url);
  //     return true;
  //   } catch {
  //     return false;
  //   }
  // };



  // Video fullscreen modal state
  const [fullscreenVideo, setFullscreenVideo] = useState(null);

  // Description preview modal state
  const [descriptionPreview, setDescriptionPreview] = useState({
    open: false,
    html: '',
    title: 'Description Preview',
    viewItems: null,
    activeIndex: 0,
  });
  const [previewContentVisible, setPreviewContentVisible] = useState(true);
  const previewPaginationRef = useRef(null);
  const previewSlideInitialRef = useRef(true);
  const previewPaginationDragRef = useRef({ didDrag: false });

  // Video playback state
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const videoRefs = useRef({});

  // Audio playback state
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [audioProgress, setAudioProgress] = useState({});
  const audioRefs = useRef({});



  // Guard flags to prevent duplicate API calls
  const isLoadingRef = useRef(false);
  const lastCallParamsRef = useRef({ lessonId: null, page: null });

  // Tracks ObjectIds of existing view_items the user removes while the
  // Edit modal is open. Sent as `removed_view_items` on submit so the server
  // knows which items to delete. Reset whenever the form is reset/opened.
  // Using a ref (not state) so deletions don't trigger re-renders.
  const removedViewItemIdsRef = useRef([]);

  // Per-item field cache so Custom ↔ HTML type switches never lose filled data.
  // Keyed by view item _id.
  const viewItemFieldsCacheRef = useRef({});

  // Debug: Log params to check if lessonId is being received
  useEffect(() => {
    // console.log('LessonContent params:', { moduleId, chapterId, lessonId });
    if (!lessonId) {
      // console.warn('Lesson ID is undefined! Check route params.');
      // console.warn('Current URL:', window.location.pathname);
      // console.warn('Expected route pattern: /module/:moduleId/chapters/:chapterId/lesson/:lessonId');
    }
  }, [moduleId, chapterId, lessonId]);

  const loadLessonContents = useCallback(
    async (forceRefresh = false, silent = false) => {
      if (!lessonId) {
        setLessons([]);
        setTotalPages(1);
        setTotalCount(0);
        if (!silent) setIsLoading(false);
        setHasLoadedOnce(true);
        return;
      }

      const lastParams = lastCallParamsRef.current;

      const isSameLessonIdAndPage = lastParams.lessonId === lessonId && lastParams.page === page;

      if (!forceRefresh && (isSameLessonIdAndPage || isLoadingRef.current)) {
        return;
      }

      if (!silent) setIsLoading(true);

      lastCallParamsRef.current = { lessonId, page };
      isLoadingRef.current = true;

      try {
        // Server-side pagination (same API supports page/page_size).
        const response = await fetchLessonContentList(lessonId, page, limit, '', {});

        let fetchedLessons = [];
        let totalFromResponse = 0;
        let totalPagesFromResponse = null;

        if (response && response.data) {
          const responseData = response.data.data ?? response.data;
          if (Array.isArray(responseData)) {
            fetchedLessons = responseData;
          } else if (
            responseData &&
            (responseData.lessons ||
              responseData.results ||
              responseData.data)
          ) {
            fetchedLessons =
              responseData.lessons ||
              responseData.results ||
              responseData.data ||
              [];
          }
        } else if (Array.isArray(response)) {
          fetchedLessons = response;
        } else if (response && (response.lessons || response.results)) {
          fetchedLessons =
            response.lessons || response.results || [];
        }

        // Attempt to extract pagination totals from multiple possible response shapes.
        // (Other views in this project use `response.pagination` and sometimes `response.total/totalPages`.)
        const pagination =
          response?.pagination ||
          response?.data?.pagination ||
          response?.data?.data?.pagination ||
          null;

        totalFromResponse =
          pagination?.totalCount ??
          pagination?.total ??
          response?.total ??
          response?.data?.total ??
          0;

        totalPagesFromResponse =
          pagination?.totalPages ??
          response?.totalPages ??
          response?.data?.totalPages ??
          null;

        const filteredContents = fetchedLessons.filter((content) => {
          const contentLessonId =
            content.lesson_id ||
            content.lessonId ||
            content.lesson?.lesson_id ||
            content.lesson?.id;
          return (
            !contentLessonId ||
            String(contentLessonId) === String(lessonId)
          );
        });

        setLessons(filteredContents);
        setTotalCount(totalFromResponse || filteredContents.length);

        const computedTotalPages =
          totalPagesFromResponse ??
          (totalFromResponse
            ? Math.ceil(totalFromResponse / (pagination?.pageSize || limit))
            : Math.max(1, Math.ceil((filteredContents.length || 0) / limit)));

        setTotalPages(computedTotalPages);

        lessonContentCache.lessons = filteredContents;
        lessonContentCache.lessonId = lessonId;
        lessonContentCache.page = page;
        lessonContentCache.totalPages = computedTotalPages;
        lessonContentCache.totalCount = totalFromResponse || filteredContents.length;

        // If the current page is beyond the last page (e.g., after delete), clamp it.
        if (page > computedTotalPages) {
          setPage(computedTotalPages);
        }
      } catch (err) {
        setLessons([]);
        setTotalPages(1);
        setTotalCount(0);
        lessonContentCache.lessons = null;
        lessonContentCache.lessonId = null;
        lessonContentCache.page = 1;
        lessonContentCache.totalPages = 1;
        lessonContentCache.totalCount = 0;
      } finally {
        if (!silent) setIsLoading(false);
        setHasLoadedOnce(true);
        isLoadingRef.current = false;
      }
    },
    [lessonId, page, limit]
  );

  useEffect(() => {
    const hasCache =
      lessonContentCache.lessons !== null &&
      lessonContentCache.lessonId === lessonId &&
      lessonContentCache.page === page;

    if (hasCache && lessonId) {
      setLessons(lessonContentCache.lessons || []);
      setHasLoadedOnce(true);
      setIsLoading(false);
      isLoadingRef.current = false;
      setTotalPages(lessonContentCache.totalPages || 1);
      setTotalCount(lessonContentCache.totalCount || 0);
      lastCallParamsRef.current = { lessonId, page };

      // Revalidate in the background so derived fields like `child_count` update
      // after navigating back from the quiz page (without requiring a full refresh).
      // Cache gives instant UI, network ensures freshness.
      setTimeout(() => {
        loadLessonContents(true, true);
      }, 0);
      return;
    }

    // Reset state when lessonId changes to prevent data leakage
    if (lastCallParamsRef.current.lessonId !== lessonId) {
      setLessons([]);
      setTotalPages(1);
      setTotalCount(0);
      setHasLoadedOnce(false);
      lastCallParamsRef.current = { lessonId: null, page: null };
    }
  }, [lessonId, page, loadLessonContents]);

  useEffect(() => {
    if (lessonId) {
      loadLessonContents();
    }
  }, [lessonId, page, loadLessonContents]);

  // Reset pagination when moving to a different lesson.
  useEffect(() => {
    setPage(1);
  }, [lessonId]);

  const moduleLabel = moduleId || 'Module';
  const chapterLabel = chapterId || 'Chapter';

  // Memoized filtered lesson contents - ensure only contents for current lesson are shown
  const filteredLessons = useMemo(() => {
    if (!lessonId || !lessons.length) {
      return [];
    }

    // Filter lesson contents to ensure they belong to the current lesson
    const filtered = lessons.filter((content) => {
      const contentLessonId = content.lesson_id || content.lessonId || content.lesson?.lesson_id || content.lesson?.id;
      // If lesson_id is missing, we assume it belongs to current lesson (for API issues)
      // Otherwise, convert both to strings for comparison to handle number/string mismatches
      return !contentLessonId || String(contentLessonId) === String(lessonId);
    });

    // Sort by order field (ascending) to display in correct sequence
    return filtered.sort((a, b) => {
      const orderA = a.order || 0;
      const orderB = b.order || 0;
      return orderA - orderB;
    });
  }, [lessons, lessonId]);

  const lessonsToRender = filteredLessons;

  // Pagination range calculation (same UI behavior as Modules page)
  const getPaginationRange = () => buildPaginationRange(page, totalPages);

  const handleBack = () => {
    // Go back to the lessons list page for the current chapter, if available
    if (moduleId && chapterId) {
      navigate(`/module/${moduleId}/chapters/${chapterId}/lessons`, {
        state: {
          fromName: chapterName,
          moduleName,
          chapterName,
        },
      });
    } else if (moduleId) {
      // Fallback: go to chapter list for the module
      navigate(`/module/${moduleId}/chapters`, {
        state: {
          fromName: moduleName,
          moduleName,
        },
      });
    } else {
      // Fallback: go to main module page
      navigate('/module');
    }
  };

  const resetForm = () => {
    removedViewItemIdsRef.current = [];
    viewItemFieldsCacheRef.current = {};
    formData.view_items.forEach((item) => {
      if (item?.imagePreview && String(item.imagePreview).startsWith('blob:')) {
        URL.revokeObjectURL(item.imagePreview);
      }
    });
    setFormData({
      id: null,
      originalType: null,
      title: '',
      subtitle: '',
      description: '',
      points: '0',
      imagePreviewUrl: '',
      videoPreviewUrl: '',
      audioFile: null,
      audioUploadedUrl: '',
      audioText: '',
      audioImage: '',
      audioDescription: '',
      order: 1,
      view_type: 'normal_view',
      view_items: [],
      translation: '',
      meaning: '',
      pronunciation: '',
      quick_tip: '',
      practice_guidance: '',
    });
    setFormErrors({
      title: '',
      description: '',
      points: '',
      audio: '',
      order: '',
      view_items: '',
      translation: '',
    });
    setMediaResetKey((key) => key + 1);
    setUploading({
      image: false,
      video: false,
      audio: false,
      audioImage: false,
    });
    setUploadErrors({
      image: '',
      video: '',
      audio: '',
      audioImage: '',
    });
  };

  const handleAddLesson = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleViewTypeChange = (viewType) => {
    // Keep view_items in state regardless of view_type so the user can
    // switch back to Highlights/Cards and recover their previously added items.
    // The payload only sends view_items when view_type !== 'normal_view'
    // (see handleFormSubmit), so this is safe — view_items are inert on Normal View.
    setFormData((prev) => ({
      ...prev,
      view_type: viewType,
    }));
    if (formErrors.view_items) {
      setFormErrors((prev) => ({ ...prev, view_items: '' }));
    }
  };

  const addViewItem = () => {
    setFormData((prev) => {
      const newItem = {
        _id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: 'custom',
        content: '',
        image: '',
        imageFile: null,
        imagePreview: '',
        text: '',
        text_size: '',
        order: prev.view_items.length,
      };
      cacheViewItemFields(newItem);
      return {
        ...prev,
        view_items: [...prev.view_items, newItem],
      };
    });
  };

  const cacheViewItemFields = (item) => {
    if (!item?._id) return;
    const prevCache = viewItemFieldsCacheRef.current[item._id] || {};
    viewItemFieldsCacheRef.current[item._id] = {
      ...prevCache,
      content: item.content ?? prevCache.content ?? '',
      image: item.image ?? prevCache.image ?? '',
      imageFile: item.imageFile !== undefined ? item.imageFile : (prevCache.imageFile ?? null),
      imagePreview: item.imagePreview ?? prevCache.imagePreview ?? '',
      text: item.text ?? prevCache.text ?? '',
      text_size: item.text_size ?? prevCache.text_size ?? '',
    };
  };

  const updateViewItem = (index, patch) => {
    setFormData((prev) => ({
      ...prev,
      view_items: prev.view_items.map((item, i) => {
        if (i !== index) return item;
        // Revoke old blob preview when it is being replaced/cleared
        if (
          item.imagePreview &&
          String(item.imagePreview).startsWith('blob:') &&
          ('imagePreview' in patch) &&
          patch.imagePreview !== item.imagePreview
        ) {
          URL.revokeObjectURL(item.imagePreview);
        }
        const next = { ...item, ...patch };
        cacheViewItemFields(next);
        return next;
      }),
    }));
    if (formErrors.view_items) {
      setFormErrors((prev) => ({ ...prev, view_items: '' }));
    }
  };

  /** Switch Custom/HTML while restoring previously filled fields for that item. */
  const handleViewItemTypeChange = (index, nextType) => {
    const normalized = normalizeViewItemType(nextType);
    setFormData((prev) => ({
      ...prev,
      view_items: prev.view_items.map((item, i) => {
        if (i !== index) return item;
        // Snapshot current values before switching
        cacheViewItemFields(item);
        const cached = viewItemFieldsCacheRef.current[item._id] || {};
        const restored = {
          ...item,
          type: normalized,
          content: cached.content ?? item.content ?? '',
          image: cached.image ?? item.image ?? '',
          imageFile: cached.imageFile !== undefined ? cached.imageFile : (item.imageFile ?? null),
          imagePreview: cached.imagePreview ?? item.imagePreview ?? '',
          text: cached.text ?? item.text ?? '',
          text_size: cached.text_size ?? item.text_size ?? '',
        };
        cacheViewItemFields(restored);
        return restored;
      }),
    }));
    if (formErrors.view_items) {
      setFormErrors((prev) => ({ ...prev, view_items: '' }));
    }
  };

  /** Store file locally; upload happens on Add/Update submit. */
  const handleViewItemImageSelect = (index, file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      Toast.error('Please choose an image file');
      return;
    }
    updateViewItem(index, {
      imageFile: file,
      imagePreview: URL.createObjectURL(file),
    });
  };

  const handleViewItemImageClear = (index) => {
    updateViewItem(index, {
      image: '',
      imageFile: null,
      imagePreview: '',
    });
  };

  const removeViewItem = (index) => {
    setFormData((prev) => {
      const removed = prev.view_items[index];
      if (removed?.imagePreview && String(removed.imagePreview).startsWith('blob:')) {
        URL.revokeObjectURL(removed.imagePreview);
      }
      if (removed?._id) {
        delete viewItemFieldsCacheRef.current[removed._id];
      }
      // If the removed item was an existing server-side item (real ObjectId),
      // record its _id so the next submit can tell the server to delete it
      // via `removed_view_items`. New (temp-*) items have no server record.
      if (removed && removed._id && isValidObjectId(removed._id)) {
        if (!removedViewItemIdsRef.current.includes(removed._id)) {
          removedViewItemIdsRef.current = [
            ...removedViewItemIdsRef.current,
            removed._id,
          ];
        }
      }
      return {
        ...prev,
        view_items: prev.view_items
          .filter((_, i) => i !== index)
          .map((item, i) => ({ ...item, order: i })),
      };
    });
    if (formErrors.view_items) {
      setFormErrors((prev) => ({ ...prev, view_items: '' }));
    }
  };

  const handleViewItemsReorder = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setFormData((prev) => {
      const oldIndex = prev.view_items.findIndex((item) => item._id === active.id);
      const newIndex = prev.view_items.findIndex((item) => item._id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return {
        ...prev,
        view_items: arrayMove(prev.view_items, oldIndex, newIndex).map((item, i) => ({
          ...item,
          order: i,
        })),
      };
    });
  };

  // const canAddLessonContent = filteredLessons.length === 0;


  const handleFormSubmit = async (isEdit = false) => {
    const errors = {
      title: '',
      description: '',
      points: '',
      audio: '',
      order: '',
      view_items: '',
      translation: '',
    };
    let hasErrors = false;

    // Validate lessonId
    if (!lessonId) {
      alert('Lesson ID is missing. Please navigate from a valid lesson page.');
      return;
    }

    if (!isValidObjectId(lessonId)) {
      alert(`Invalid lesson ID format: "${lessonId}". Lesson ID must be a 24 character hex string.`);
      // console.error('Invalid lessonId:', lessonId);
      return;
    }

    // Validate title — the only required field. All other fields are optional.
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
      hasErrors = true;
    }

    // Validate points (optional, but if provided must be a non-negative number)
    if (formData.points !== '' && formData.points !== null && formData.points !== undefined) {
      const parsedPoints = Number(formData.points);
      if (Number.isNaN(parsedPoints) || parsedPoints < 0) {
        errors.points = 'Points must be 0 or more';
        hasErrors = true;
      }
    }

    // View items required for highlights/cards; validate html vs custom rows
    if (formData.view_type === 'highlights_view' || formData.view_type === 'cards_view') {
      if (!formData.view_items.length) {
        errors.view_items = 'Add at least one view item';
        hasErrors = true;
      } else {
        const invalidIndex = formData.view_items.findIndex((item) => {
          const type = normalizeViewItemType(item.type);
          if (type === 'custom') {
            const missingContent = !(
              String(item.image || '').trim() ||
              item.imageFile ||
              String(item.text || '').trim()
            );
            const badTextSize = !isValidTextSizeInt(item.text_size);
            return missingContent || badTextSize;
          }
          return !String(item.content || '').trim();
        });
        if (invalidIndex >= 0) {
          const badItem = formData.view_items[invalidIndex];
          const type = normalizeViewItemType(badItem.type);
          if (type === 'custom' && !isValidTextSizeInt(badItem.text_size)) {
            errors.view_items = `Item ${invalidIndex + 1}: text size must be a positive whole number`;
          } else if (type === 'custom') {
            errors.view_items = `Item ${invalidIndex + 1}: add an image or text`;
          } else {
            errors.view_items = `Item ${invalidIndex + 1}: HTML content is required`;
          }
          hasErrors = true;
        }
      }
    }

    setFormErrors(errors);
    if (hasErrors) return;

    if (isEdit) {
      setEditing(true);
    } else {
      setAdding(true);
    }

    try {
      const audioUrl = (formData.audioUploadedUrl || '').trim();

      let contentType = '';
      if (isEdit && formData.originalType) {
        contentType = formData.originalType;
      } else if (formData.view_type === 'mantra') {
        contentType = 'mantra';
      } else if (audioUrl) {
        contentType = 'audio';
      } else {
        contentType = 'image';
      }

      // Upload pending view-item images only on Add/Update (not on file select).
      // Upload even if current type is HTML so custom image data is not lost.
      let resolvedViewItems = formData.view_items;
      if (
        formData.view_type === 'highlights_view' ||
        formData.view_type === 'cards_view'
      ) {
        const pendingUploads = formData.view_items.some((item) => item.imageFile);
        if (pendingUploads) {
          resolvedViewItems = await Promise.all(
            formData.view_items.map(async (item) => {
              if (!item.imageFile) return item;
              const url = await uploadImage(item.imageFile, {
                folder: 'uploads/LessonContent',
              });
              const next = {
                ...item,
                image: url,
                imageFile: null,
              };
              cacheViewItemFields(next);
              return next;
            })
          );
        }
      }

      const payload = {
        lesson_id: lessonId,
        title: formData.title ? formData.title.trim() : '',
        subtitle: formData.subtitle ? formData.subtitle.trim() : '',
        type: contentType,
        points: formData.points === '' || formData.points === null || formData.points === undefined ? 0 : Number(formData.points),
        view_type: formData.view_type || 'normal_view',
        view_items: [],
      };

      if (formData.view_type === 'mantra') {
        payload.translation = (formData.translation || '').trim();
        payload.meaning = (formData.meaning || '').trim();
        payload.pronunciation = (formData.pronunciation || '').trim();
        payload.quick_tip = (formData.quick_tip || '').trim();
        payload.practice_guidance = (formData.practice_guidance || '').trim();
      } else {
        payload.audio = audioUrl;
        payload.audioText = (formData.audioText || '').trim();
        payload.audioImage = (formData.audioImage || '').trim();
        payload.audioDescription = (formData.audioDescription || '').trim();
        payload.imageText = (formData.description || '').trim();
        payload.view_items =
          !formData.view_type || formData.view_type === 'normal_view'
            ? []
            : resolvedViewItems.map((item, index) => {
                // Always send both HTML + custom fields so updating with one
                // type selected does not wipe the other type's saved data.
                const cached = viewItemFieldsCacheRef.current[item._id] || {};
                const type = normalizeViewItemType(item.type);
                const content = String(
                  item.content ?? cached.content ?? ''
                ).trim();
                const image = String(item.image ?? cached.image ?? '').trim();
                const text = String(item.text ?? cached.text ?? '').trim();
                const textSize = sanitizeTextSizeInput(
                  item.text_size ?? cached.text_size ?? ''
                );

                const base = {
                  type,
                  content: content || null,
                  image: image || null,
                  text: text || null,
                  text_size: textSize || null,
                  order: index,
                };
                // On edit, keep _id for items that already exist on the server
                // (so it can match them) and omit it for newly added items
                // (their _id is a client-side temp-* placeholder, not a real
                // Mongo ObjectId) so the server assigns a fresh one.
                if (item._id && isValidObjectId(item._id)) {
                  return { _id: item._id, ...base };
                }
                return base;
              });
      }

      // On edit, tell the server which existing view_items the user removed
      // while the modal was open so it can delete them. Empty array on add.
      if (isEdit) {
        payload.removed_view_items = removedViewItemIdsRef.current;
      }

      // Handle edit vs create
      if (isEdit) {
        // For edit mode, ensure we have lesson_id and type to identify the record
        if (!lessonId || !payload.type) {
          // console.error('Missing lesson_id or type. lessonId:', lessonId, 'type:', payload.type);
          alert('Error: Lesson ID or type is missing. Cannot update lesson .');
          setEditing(false);
          return;
        }
        // API identifies content by lesson_id and type combination (no id field needed)
        // Payload matches curl format exactly
        // console.log('Update payload:', payload);
        const updateResponse = await updateLessonContent(formData.id, payload);

        // console.log('Update response:', updateResponse);
        // Only close modal and reload if update succeeds
        setShowEditModal(false);
        resetForm();
        // Clear cache completely to force fresh data load
        lessonContentCache.lessons = null;
        lessonContentCache.lessonId = null;
        lastCallParamsRef.current = { lessonId: null };
        isLoadingRef.current = false;
        // Clear current lessons state to show loading
        setLessons([]);
        setIsLoading(true);
        // Force reload with a delay to ensure API has processed the update
        setTimeout(async () => {
          try {
            await loadLessonContents(true);
          } catch (error) {
            // console.error('Error reloading after update:', error);
            setIsLoading(false);
          }
        }, 500);
      } else {
        // Create new lesson content
        await createLessonContent(payload);
        // Optimistic: bump the unit's lessons count so Units page updates instantly when navigating back.
        bumpUnitLessonsCount(lessonId, 1);
        // Only close modal and reload if create succeeds
        setShowAddModal(false);
        resetForm();
        await loadLessonContents(true);
      }
    } catch (err) {
      // Show error message - don't create new record if update fails
      const errorMessage = err.response?.data?.message || err.message ||
        (isEdit ? 'Failed to update lesson content' : 'Failed to add lesson ');
      alert(errorMessage);
      // Don't close modal or reload on error - let user fix and retry
    } finally {
      if (isEdit) {
        setEditing(false);
      } else {
        setAdding(false);
      }
    }
  };

  const getCommonDescription = (lesson = {}) =>
    lesson.imageText || '';

  const escapeHtml = (str = '') =>
    String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const handleEdit = (contentIdOrLesson) => {
    // Accept either contentId or lesson object directly
    let lesson;
    if (typeof contentIdOrLesson === 'object' && contentIdOrLesson !== null) {
      // If lesson object is passed directly, use it
      lesson = contentIdOrLesson;
    } else {
      // Otherwise, find by ID from current lessons state
      const allLessons = filteredLessons.length > 0 ? filteredLessons : lessons;
      lesson = allLessons.find((l) => (l.id === contentIdOrLesson) || (l._id === contentIdOrLesson));
    }

    if (!lesson) {
      // console.warn('Lesson not found for contentId:', contentIdOrLesson);
      return;
    }
    // console.log('Editing lesson:', lesson);

    // Get the content ID (handle both id and _id)
    const lessonContentId = lesson.id || lesson._id || contentIdOrLesson;

    const description =
      lesson.imageText ||
      lesson.image_text ||
      lesson.description ||
      '';
    const audioUrl = lesson.audio || '';
    const originalType = lesson.type || lesson.content_type || '';
    const audioTitle = lesson.audioText != null && lesson.audioText !== ''
      ? lesson.audioText
      : '';
    const audioImage = lesson.audioImage != null && lesson.audioImage !== ''
      ? lesson.audioImage
      : '';
    const audioDescription = lesson.audioDescription != null && lesson.audioDescription !== ''
      ? lesson.audioDescription
      : '';

    removedViewItemIdsRef.current = [];
    viewItemFieldsCacheRef.current = {};
    setFormData({
      id: lessonContentId,
      originalType: originalType,
      title: lesson.title || '',
      subtitle: lesson.subtitle || '',
      description: description,
      points: lesson.points ?? '',
      imagePreviewUrl: '',
      videoPreviewUrl: '',
      audioFile: null,
      audioUploadedUrl: audioUrl,
      audioText: audioTitle,
      audioImage: audioImage,
      audioDescription: audioDescription,
      order: lesson.order || 1,
      // Preserve view_type/view_items on edit so a roundtrip doesn't wipe them.
      view_type: lesson.view_type || 'normal_view',
      view_items: Array.isArray(lesson.view_items)
        ? lesson.view_items.map((item, i) => {
            // Legacy API rows may be plain HTML strings
            let normalized;
            if (typeof item === 'string') {
              normalized = {
                _id: `temp-edit-${Date.now()}-${i}`,
                type: 'html',
                content: item,
                image: '',
                imageFile: null,
                imagePreview: '',
                text: '',
                text_size: '',
                order: i,
              };
            } else {
              normalized = {
                _id: item._id || `temp-edit-${Date.now()}-${i}`,
                type: normalizeViewItemType(item.type),
                content: item.content || '',
                image: item.image || '',
                imageFile: null,
                imagePreview: '',
                text: item.text || '',
                text_size: parseTextSizeFromApi(item.text_size),
                order: item.order ?? i,
              };
            }
            cacheViewItemFields(normalized);
            return normalized;
          })
        : [],
      translation: lesson.translation || '',
      meaning: lesson.meaning || '',
      pronunciation: lesson.pronunciation || '',
      quick_tip: lesson.quick_tip || '',
      practice_guidance: lesson.practice_guidance || '',
    });

    setFormErrors({
      title: '',
      description: '',
      points: '',
      audio: '',
      order: '',
      view_items: '',
      translation: '',
    });
    setMediaResetKey((key) => key + 1);
    setUploading({
      image: false,
      video: false,
      audio: false,
      audioImage: false,
    });
    setUploadErrors({
      image: '',
      video: '',
      audio: '',
      audioImage: '',
    });
    setShowEditModal(true);
  };


  const handleDelete = (contentIdOrLesson) => {
    // Accept either contentId or lesson object directly (similar to handleEdit)
    let lesson;
    if (typeof contentIdOrLesson === 'object' && contentIdOrLesson !== null) {
      lesson = contentIdOrLesson;
    } else {
      // Find by ID from current lessons state
      const allLessons = filteredLessons.length > 0 ? filteredLessons : lessons;
      lesson = allLessons.find((l) => (l.id === contentIdOrLesson) || (l._id === contentIdOrLesson));
    }

    if (!lesson) {
      // console.warn('Lesson not found for contentId:', contentIdOrLesson);
      return;
    }

    setLessonToDelete(lesson);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!lessonToDelete) {
      setShowDeleteModal(false);
      return;
    }

    const contentId = lessonToDelete.id || lessonToDelete._id;
    if (!contentId) {
      alert('Lesson ID is missing. Cannot delete lesson .');
      return;
    }

    setDeleting(true);
    try {
      // Call delete API - matches curl: DELETE /api/v1/admin/lesson-content/{contentId}
      // Authorization header is automatically added by apiClient
      await deleteLessonContent(contentId);
      // Optimistic: decrement the unit's lessons count so Units page updates instantly when navigating back.
      bumpUnitLessonsCount(lessonId, -1);
      setShowDeleteModal(false);
      setLessonToDelete(null);
      // Clear cache and reload lesson contents
      lessonContentCache.lessons = null;
      lessonContentCache.lessonId = null;
      lastCallParamsRef.current = { lessonId: null };
      isLoadingRef.current = false;
      await loadLessonContents(true);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete lesson ';
      alert(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setLessonToDelete(null);
  };

  const handleSaveLessonContentOrder = async () => {
    // Validate lessonId is present
    if (!lessonId) {
      Toast.error('Lesson ID is missing. Cannot save lesson order.');
      return;
    }

    setReorderSaveLoading(true);
    try {
      const payload = reorderLessonContents.map((lc, index) => ({
        contentId: String(lc.id || lc._id || lc.content_id),
        order: index + 1,
      }));

      // Update each lesson content's order via API
      const updatePromises = payload.map(async (item) => {
        const lessonContent = reorderLessonContents.find(
          (lc) => String(lc.id || lc._id || lc.content_id) === item.contentId
        );
        if (lessonContent) {
          const typeLabel = lessonContent.type || lessonContent.content_type || 'audio';
          const audioUrl = lessonContent.audio || '';
          const rawPoints = lessonContent.points;
          const pointsNum = rawPoints === '' || rawPoints === null || rawPoints === undefined
            ? 0
            : Number(rawPoints);

          const updatePayload = {
            lesson_id: lessonId,
            title: lessonContent.title || '',
            type: typeLabel,
            points: Number.isNaN(pointsNum) ? 0 : pointsNum,
            audio: audioUrl,
            audioText: lessonContent.audioText != null && lessonContent.audioText !== ''
              ? lessonContent.audioText
              : '',
            audioImage: lessonContent.audioImage != null && lessonContent.audioImage !== ''
              ? lessonContent.audioImage
              : '',
            audioDescription: lessonContent.audioDescription != null && lessonContent.audioDescription !== ''
              ? lessonContent.audioDescription
              : '',
            order: item.order,
          };

          return updateLessonContent(item.contentId, updatePayload);
        }
      });

      await Promise.all(updatePromises);
      setShowReorderModal(false);
      Toast.success('Lesson order updated successfully');
      // Clear cache completely to force fresh data load
      lessonContentCache.lessons = null;
      lessonContentCache.lessonId = null;
      lastCallParamsRef.current = { lessonId: null };
      isLoadingRef.current = false;
      setLessons([]);
      setIsLoading(true);
      setTimeout(async () => {
        try {
          await loadLessonContents(true);
        } catch (error) {
          // console.error('Error reloading after reorder:', error);
        }
      }, 500);
    } catch (err) {
      // console.error(err);
      Toast.error('Failed to save lesson order');
    } finally {
      setReorderSaveLoading(false);
    }
  };

  const handleUploadCSV = async () => {
    if (!selectedFile) {
      Toast.error('Please select a JSON file');
      return;
    }
    if (!moduleId || !chapterId || !lessonId) {
      Toast.error('Module, chapter and lesson are required.');
      return;
    }
    setUploadingCsv(true);
    try {
      const res = await uploadQuizCsv(selectedFile, {
        module_id: moduleId,
        chapter_id: chapterId,
        lesson_id: lessonId,
      });
      const resultData = res?.data || {};
      setUploadResult({
        successful: resultData.successful || [],
        skipped: resultData.skipped || [],
        error: resultData.error || [],
      });
      setShowUploadModal(false);
      setSelectedFile(null);
      setShowUploadResultModal(true);
      lessonContentCache.lessons = null;
      lastCallParamsRef.current = { lessonId: null };
      await loadLessonContents(true);
    } catch (err) {
      Toast.error(err.message || 'Failed to upload JSON');
    } finally {
      setUploadingCsv(false);
    }
  };

  // Custom Grid Icon Component (3 rows, 2 dots each - vertical layout)
  const GridIcon = ({ size = 18 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-gray-400"
    >
      {/* Left column - 3 dots */}
      <circle cx="4.5" cy="3" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="9" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="15" r="1.5" fill="currentColor" />
      {/* Right column - 3 dots */}
      <circle cx="13.5" cy="3" r="1.5" fill="currentColor" />
      <circle cx="13.5" cy="9" r="1.5" fill="currentColor" />
      <circle cx="13.5" cy="15" r="1.5" fill="currentColor" />
    </svg>
  );

  const SortableLessonContentItem = ({ lessonContent, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({
      id: lessonContent.id || lessonContent._id || lessonContent.content_id,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const commonDescription = getCommonDescription(lessonContent);
    const lessonName = lessonContent.title || commonDescription || lessonContent.lesson_name || 'Untitled Lesson';

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="flex items-center justify-between p-4 bg-[#FDF6EF] rounded-xl border border-white/10 cursor-grab active:cursor-grabbing touch-none"
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="text-black flex items-center justify-center text-sm font-semibold min-w-[20px]">
            {index + 1}
          </div>

          {/* <div className="w-10 h-10 rounded-lg bg-brand text-black flex items-center justify-center font-bold flex-shrink-0">
            {lessonName.charAt(0)}
          </div> */}

          <p className="text-black font-medium  flex-1  min-w-0 break-words line-clamp-2">
            {lessonName}
          </p>
        </div>

        <div className="flex-shrink-0 ml-2">
          <GridIcon size={18} />
        </div>
      </div>
    );
  };

  const renderAudioHomeFields = (idPrefix) => (
    <>
      <UrlFileToggleField
        id={`${idPrefix}-audio-image`}
        label="Audio image"
        value={formData.audioImage}
        error={uploadErrors.audioImage}
        onChange={(url) => {
          setFormData((prev) => ({ ...prev, audioImage: url }));
          setUploadErrors((prev) => ({ ...prev, audioImage: '' }));
        }}
        onClear={() => {
          setFormData((prev) => ({ ...prev, audioImage: '' }));
          setUploadErrors((prev) => ({ ...prev, audioImage: '' }));
        }}
        onUploadingChange={(isUploading) => setUploading((prev) => ({ ...prev, audioImage: isUploading }))}
        disabled={adding || editing}
        resetKey={mediaResetKey}
        accept="image/*"
        uploadFn={uploadImage}
        uploadingLabel="Uploading image..."
        urlHint="Optional HTTP/HTTPS URL. Clear removes a saved image."
        fileHint="Optional image file. Uses the shared Admin upload helper."
        switchToFileHint="Switch left to choose a file from your device"
        switchToUrlHint="Switch right to paste an image URL."
        preview="image"
      />
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Audio description
        </label>
        <textarea
          value={formData.audioDescription}
          onChange={(e) => setFormData({ ...formData, audioDescription: e.target.value })}
          placeholder="Description shown with this audio"
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
        />
      </div>
    </>
  );

  const renderAudioFileField = (idPrefix) => (
    <UrlFileToggleField
      id={`${idPrefix}-audio-file`}
      label="Audio file"
      value={formData.audioUploadedUrl}
      error={uploadErrors.audio || formErrors.audio}
      onChange={(url) => {
        setFormData((prev) => ({ ...prev, audioUploadedUrl: url, audioFile: null }));
        setUploadErrors((prev) => ({ ...prev, audio: '' }));
        if (formErrors.audio) setFormErrors((prev) => ({ ...prev, audio: '' }));
      }}
      onClear={() => {
        setFormData((prev) => ({ ...prev, audioUploadedUrl: '', audioFile: null }));
        setUploadErrors((prev) => ({ ...prev, audio: '' }));
      }}
      onUploadingChange={(isUploading) => setUploading((prev) => ({ ...prev, audio: isUploading }))}
      disabled={adding || editing}
      resetKey={mediaResetKey}
      accept="audio/*"
      uploadFn={uploadAssetFile}
      uploadingLabel="Uploading audio..."
      urlHint="Optional HTTP/HTTPS audio URL. Clear removes a saved file."
      fileHint="Optional audio file. Uses the shared Admin upload helper."
      switchToFileHint="Switch left to choose an audio file from your device"
      switchToUrlHint="Switch right to paste an audio URL."
      preview="audio"
    />
  );

  const previewNotSavedLabel = (name) => (
    <>
      {name}{' '}
      <span className="text-gray-400 font-normal">(optional preview, not saved)</span>
    </>
  );

  const renderPreviewMediaFields = (idPrefix) => (
    <>
      <UrlFileToggleField
        id={`${idPrefix}-preview-image`}
        label={previewNotSavedLabel('Image')}
        value={formData.imagePreviewUrl}
        error={uploadErrors.image}
        onChange={(url) => {
          setFormData((prev) => ({ ...prev, imagePreviewUrl: url }));
          setUploadErrors((prev) => ({ ...prev, image: '' }));
        }}
        onClear={() => {
          setFormData((prev) => ({ ...prev, imagePreviewUrl: '' }));
          setUploadErrors((prev) => ({ ...prev, image: '' }));
        }}
        onUploadingChange={(isUploading) => setUploading((prev) => ({ ...prev, image: isUploading }))}
        disabled={adding || editing}
        resetKey={mediaResetKey}
        accept="image/*"
        uploadFn={uploadImage}
        uploadingLabel="Uploading image..."
        urlHint="Preview only — not saved with the lesson. HTTP/HTTPS URL."
        fileHint="Preview only — not saved with the lesson."
        switchToFileHint="Switch left to choose a file from your device"
        switchToUrlHint="Switch right to paste an image URL."
        preview="image"
        imagePreviewClass="w-full h-32 object-cover rounded-lg border border-gray-200"
      />
      <UrlFileToggleField
        id={`${idPrefix}-preview-video`}
        label={previewNotSavedLabel('Video')}
        value={formData.videoPreviewUrl}
        error={uploadErrors.video}
        onChange={(url) => {
          setFormData((prev) => ({ ...prev, videoPreviewUrl: url }));
          setUploadErrors((prev) => ({ ...prev, video: '' }));
        }}
        onClear={() => {
          setFormData((prev) => ({ ...prev, videoPreviewUrl: '' }));
          setUploadErrors((prev) => ({ ...prev, video: '' }));
        }}
        onUploadingChange={(isUploading) => setUploading((prev) => ({ ...prev, video: isUploading }))}
        disabled={adding || editing}
        resetKey={mediaResetKey}
        accept="video/*"
        uploadFn={async (file) => {
          if (file.type && !file.type.startsWith('video/')) {
            throw new Error('Please choose a video file.');
          }
          return uploadAssetFile(file);
        }}
        uploadingLabel="Uploading video..."
        urlHint="Preview only — not saved with the lesson. HTTP/HTTPS URL."
        fileHint="Preview only — not saved with the lesson."
        switchToFileHint="Switch left to choose a file from your device"
        switchToUrlHint="Switch right to paste a video URL."
        preview="video"
      />
    </>
  );

  // Audio playback handlers
  const handleAudioPlay = (audioId, audioUrl) => {
    // Stop currently playing audio if any
    if (playingAudioId && playingAudioId !== audioId) {
      const prevAudio = audioRefs.current[playingAudioId];
      if (prevAudio) {
        prevAudio.pause();
        prevAudio.currentTime = 0;
      }
    }

    const audio = audioRefs.current[audioId];
    if (!audio) {
      // Create new audio element
      const newAudio = new Audio(audioUrl);
      newAudio.addEventListener('timeupdate', () => {
        if (newAudio.duration) {
          setAudioProgress(prev => ({
            ...prev,
            [audioId]: (newAudio.currentTime / newAudio.duration) * 100
          }));
        }
      });
      newAudio.addEventListener('ended', () => {
        setPlayingAudioId(null);
        setAudioProgress(prev => ({
          ...prev,
          [audioId]: 0
        }));
      });
      audioRefs.current[audioId] = newAudio;
      newAudio.play();
      setPlayingAudioId(audioId);
    } else {
      if (playingAudioId === audioId) {
        // Pause if already playing
        audio.pause();
        setPlayingAudioId(null);
      } else {
        // Play if paused
        audio.play();
        setPlayingAudioId(audioId);
      }
    }
  };

  const handleVideoFullscreen = (videoUrl, e) => {
    e.stopPropagation();
    setFullscreenVideo(videoUrl);
  };

  const closeFullscreenVideo = () => {
    setFullscreenVideo(null);
  };

  const PREVIEW_DOCUMENT_STYLES = `
    :root {
      color-scheme: light;
    }
    body {
      margin: 0;
      padding: 16px;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      color: #111827;
      background: #ffffff;
      word-break: break-word;
      display: block;
      overflow-x: hidden;
    }
    img, video, iframe {
      max-width: 100%;
      height: auto;
      display: block;
    }
    table {
      width: 100%;
      border-collapse: collapse;
    }
    .preview-view-item {
      display: block !important;
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 0 20px 0;
      padding: 0 0 16px 0;
      border-bottom: 1px solid #e5e7eb;
      clear: both;
      box-sizing: border-box;
      float: none !important;
    }
    .preview-view-item:last-child {
      margin-bottom: 0;
      padding-bottom: 0;
      border-bottom: none;
    }
    .preview-view-item__label {
      font-weight: 600;
      margin-bottom: 8px;
      color: #374151;
    }
    .preview-view-item__content {
      display: block !important;
      width: 100% !important;
      max-width: 100% !important;
      overflow: visible;
      float: none !important;
    }
    .preview-view-item__content img {
      width: 100% !important;
      height: auto !important;
      object-fit: contain !important;
      object-position: center top !important;
      float: none !important;
      display: block !important;
    }
    .preview-view-item__content > div,
    .preview-view-item__content > section,
    .preview-view-item__content > article {
      display: block !important;
      width: 100% !important;
      max-width: 100% !important;
      float: none !important;
    }
    .preview-custom-card {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .preview-custom-card img {
      width: 100%;
      height: auto;
      border-radius: 12px;
      object-fit: contain;
      background: #f3f4f6;
    }
    .preview-custom-card__text {
      margin: 0;
      color: #111827;
      white-space: pre-wrap;
      word-break: break-word;
    }
  `;

  const isBareImageUrl = (value = '') => {
    const trimmed = String(value || '').trim();
    return (
      /^https?:\/\/.+/i.test(trimmed) &&
      !/[<>"'\s]/.test(trimmed) &&
      (/\.(png|jpe?g|gif|webp|svg|avif)(\?.*)?$/i.test(trimmed) ||
        /\/image\//i.test(trimmed) ||
        /cloudinary|s3|blob:/i.test(trimmed))
    );
  };

  const normalizeViewItemContentForPreview = (content = '') => {
    const trimmed = String(content || '').trim();
    if (!trimmed) return '';
    if (isBareImageUrl(trimmed)) {
      return `<img src="${trimmed}" alt="" />`;
    }
    return trimmed;
  };

  const wrapViewItemPreviewContent = (itemOrContent = '') => {
    // Accept either a view_item object or a legacy HTML string
    if (itemOrContent && typeof itemOrContent === 'object') {
      const item = itemOrContent;
      if (normalizeViewItemType(item.type) === 'custom') {
        const image = String(item.imagePreview || item.image || '').trim();
        const text = String(item.text || '').trim();
        const textSize = formatTextSizeForCss(item.text_size);
        const imageHtml = image
          ? `<img src="${escapeHtml(image)}" alt="" />`
          : '';
        const textHtml = text
          ? `<p class="preview-custom-card__text" style="font-size:${escapeHtml(textSize)};">${escapeHtml(text)}</p>`
          : '';
        return `<div class="preview-view-item preview-view-item--single">
          <div class="preview-view-item__content">
            <div class="preview-custom-card">${imageHtml}${textHtml}</div>
          </div>
        </div>`;
      }
      return wrapViewItemPreviewContent(item.content || '');
    }

    const normalized = normalizeViewItemContentForPreview(itemOrContent);
    if (!normalized) return '';
    const isFullDocument = /<\s*!doctype|<\s*html[\s>]|<\s*head[\s>]|<\s*body[\s>]/i.test(normalized);
    if (isFullDocument) return normalized;
    return `<div class="preview-view-item preview-view-item--single">
      <div class="preview-view-item__content">${normalized}</div>
    </div>`;
  };

  const buildDescriptionPreviewDocument = (rawHtml = '') => {
    const normalizedHtml = String(rawHtml || '').trim();
    const fallbackBody = '<p style="color:#6B7280;font-family:system-ui;margin:0;">No description available.</p>';

    if (!normalizedHtml) {
      return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
  </head>
  <body style="margin:0;padding:16px;font-family:-apple-system,BlinkMacSystemFont,&quot;Segoe UI&quot;,Roboto,&quot;Helvetica Neue&quot;,Arial,sans-serif;font-size:14px;line-height:1.5;color:#111827;background:#ffffff;word-break:break-word;">${fallbackBody}</body>
</html>`;
    }

    const isFullDocument = /<\s*!doctype|<\s*html[\s>]|<\s*head[\s>]|<\s*body[\s>]/i.test(normalizedHtml);

    if (isFullDocument) {
      return normalizedHtml;
    }

    return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>${PREVIEW_DOCUMENT_STYLES}</style>
  </head>
  <body>${normalizedHtml}</body>
</html>`;
  };

  const openDescriptionPreview = (html, title = 'Description Preview') => {
    const normalizedHtml = String(html || '');
    if (!normalizedHtml.trim()) return;

    setDescriptionPreview({
      open: true,
      html: normalizedHtml,
      title,
      viewItems: null,
      activeIndex: 0,
    });
  };

  const openViewItemsPreview = (items = [], title = 'View Items Preview', startIndex = 0) => {
    const normalizedItems = items
      .filter((it) => isViewItemPreviewable(it))
      .map((it) => ({
        ...it,
        type: normalizeViewItemType(it.type),
        content: String(it.content || '').trim(),
        image: String(it.imagePreview || it.image || '').trim(),
        imagePreview: String(it.imagePreview || '').trim(),
        text: String(it.text || '').trim(),
        text_size: String(it.text_size || '').trim(),
      }));
    if (normalizedItems.length === 0) return;

    const safeIndex = Math.min(Math.max(startIndex, 0), normalizedItems.length - 1);

    setDescriptionPreview({
      open: true,
      html: '',
      title,
      viewItems: normalizedItems,
      activeIndex: safeIndex,
    });
  };

  const setDescriptionPreviewSlide = (index) => {
    setDescriptionPreview((prev) => {
      if (!prev.viewItems?.length) return prev;
      const safeIndex = Math.min(Math.max(index, 0), prev.viewItems.length - 1);
      if (safeIndex === prev.activeIndex) return prev;
      setPreviewContentVisible(false);
      return { ...prev, activeIndex: safeIndex };
    });
  };

  const closeDescriptionPreview = () => {
    setDescriptionPreview({
      open: false,
      html: '',
      title: 'Description Preview',
      viewItems: null,
      activeIndex: 0,
    });
  };

  const descriptionPreviewDoc = useMemo(() => {
    if (descriptionPreview.viewItems?.length) {
      const activeItem = descriptionPreview.viewItems[descriptionPreview.activeIndex];
      return buildDescriptionPreviewDocument(wrapViewItemPreviewContent(activeItem));
    }
    return buildDescriptionPreviewDocument(descriptionPreview.html);
  }, [
    descriptionPreview.html,
    descriptionPreview.viewItems,
    descriptionPreview.activeIndex,
  ]);

  useEffect(() => {
    if (!descriptionPreview.open) {
      previewSlideInitialRef.current = true;
      setPreviewContentVisible(true);
      return;
    }

    if (previewSlideInitialRef.current) {
      previewSlideInitialRef.current = false;
      return;
    }

    setPreviewContentVisible(false);
    const fallbackTimer = setTimeout(() => setPreviewContentVisible(true), 400);
    return () => clearTimeout(fallbackTimer);
  }, [descriptionPreview.activeIndex, descriptionPreview.open]);

  useEffect(() => {
    if (!descriptionPreview.open || !previewPaginationRef.current) return;

    const container = previewPaginationRef.current;
    const activeButton = container.querySelector('[data-preview-page-active="true"]');
    if (!activeButton) return;

    const scrollActivePageIntoView = () => {
      const containerWidth = container.clientWidth;
      const buttonWidth = activeButton.offsetWidth;
      const containerRect = container.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      const targetScroll =
        container.scrollLeft +
        (buttonRect.left - containerRect.left) -
        containerWidth / 2 +
        buttonWidth / 2;
      const maxScroll = Math.max(0, container.scrollWidth - containerWidth);

      container.scrollTo({
        left: Math.min(Math.max(0, targetScroll), maxScroll),
        behavior: 'smooth',
      });
    };

    requestAnimationFrame(scrollActivePageIntoView);
  }, [descriptionPreview.activeIndex, descriptionPreview.open, descriptionPreview.viewItems?.length]);

  const handlePreviewIframeLoad = () => {
    setPreviewContentVisible(true);
  };

  const handlePreviewPaginationMouseDown = useCallback((e) => {
    if (e.button !== 0) return;

    const container = previewPaginationRef.current;
    if (!container || container.scrollWidth <= container.clientWidth) return;

    e.preventDefault();

    const startX = e.pageX;
    const startScrollLeft = container.scrollLeft;
    let moved = false;

    const handleMouseMove = (moveEvent) => {
      const dx = moveEvent.pageX - startX;
      if (Math.abs(dx) > 4) moved = true;
      container.scrollLeft = startScrollLeft - dx;
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      previewPaginationDragRef.current.didDrag = moved;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const handlePreviewPaginationPageClick = useCallback((index) => {
    if (previewPaginationDragRef.current.didDrag) {
      previewPaginationDragRef.current.didDrag = false;
      return;
    }
    setDescriptionPreviewSlide(index);
  }, []);

  useEffect(() => {
    const container = previewPaginationRef.current;
    if (!container || !descriptionPreview.open) return undefined;

    const handleWheel = (e) => {
      if (container.scrollWidth <= container.clientWidth) return;

      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (delta === 0) return;

      e.preventDefault();
      container.scrollLeft += delta;
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [descriptionPreview.open, descriptionPreview.viewItems?.length]);

  // Cleanup audio and video on unmount
  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
      Object.values(videoRefs.current).forEach(video => {
        if (video) {
          video.pause();
        }
      });
    };
  }, []);

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-[#FDF6EF] flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto no-scrollbar">
          {/* Header */}
          <div className="mb-4 md:mb-3 relative z-50">
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2  rounded-lg hover:bg-white/50 transition-colors"
              >
                <HiMenu className="w-6 h-6  text-gray-800" />
              </button>
              <button
                onClick={handleBack}
                className="p-2 sm:ml-2  rounded-lg hover:bg-white/50 transition-colors relative z-50"
              >
                <HiArrowLeft className="w-6 h-6 -ml-2  text-gray-800" />
              </button>
              <div className="flex flex-col md:flex-row md:items-baseline">
                <h1 className="text-xl md:text-3xl sm:mt-0.5 font-bold text-gray-800">
                  Lessons
                </h1>
                {fromName && (
                  <span className="text-sm md:text-lg font-semibold text-gray-600 mt-0.5 md:mt-0 md:ml-2">
                    {fromName}
                  </span>
                )}
              </div>
            </div>
            {/* <p className="text-sm text-gray-600">
              Lessons for module {moduleLabel} / chapter {chapterLabel}
              {lessonId ? ` / lesson ${lessonId}` : ''}.
            </p> */}
          </div>

          {/* Warning if lessonId is missing */}
          {!lessonId && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> Lesson ID is missing. Please navigate from a valid lesson page.
              </p>
              <button
                onClick={handleBack}
                className="mt-2 text-sm text-yellow-800 underline hover:text-yellow-900"
              >
                Go back to lessons list
              </button>
            </div>
          )}

          {/* Action Bar */}
          {(filteredLessons.length > 0 || (moduleId && chapterId && lessonId)) && (
            <div className="mb-5 md:mb-6 flex justify-end items-center gap-2">
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-2 px-3 py-2 mt-1.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
              >
                <HiUpload className="w-4 h-4" />
                Upload File
              </button>
              <button
                onClick={() => {
                  if (!filteredLessons.length) return;
                  setReorderLessonContents(filteredLessons);
                  setShowReorderModal(true);
                }}
                disabled={!filteredLessons.length}
                className={`flex items-center w-26 gap-2 px-3 py-2 mt-1.5 text-sm font-semibold  bg-brand text-white hover:shadow-lg rounded-xl shadow-md whitespace-nowrap flex-shrink-0 transition-all 
                
                }`}
              >
                Edit Order
              </button>
              <button
                onClick={handleAddLesson}
                className="flex items-center gap-2 px-3 py-2 mt-1.5 text-sm font-semibold text-white rounded-xl bg-brand"
              >
                + Add Lessons
              </button>
            </div>
          )}

          {/* Lesson List */}
          <div>
            {isLoading || !hasLoadedOnce ? (
              <div className="fixed inset-0 ml-0 lg:ml-64 z-40 flex items-center justify-center">
                <div className="flex items-center gap-3" role="status" aria-live="polite">
                  <svg
                    className="w-12 h-12 animate-spin"
                    viewBox="0 0 100 100"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#E9D5FF" />
                        <stop offset="25%" stopColor="#C084FC" />
                        <stop offset="50%" stopColor="#A855F7" />
                        <stop offset="75%" stopColor="#9333EA" />
                        <stop offset="100%" stopColor="#6B21A8" />
                      </linearGradient>
                    </defs>
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke="url(#purple-gradient)"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray="60 200"
                      fill="none"
                      transform="rotate(-90 50 50)"
                    />
                  </svg>
                </div>
              </div>
            ) : filteredLessons.length === 0 ? (
              <div className="fixed inset-0 ml-0 lg:ml-64 z-30 flex items-center justify-center pointer-events-none">
                <div className="max-w-md mx-auto text-center pointer-events-auto">
                  <div className="mb-6">
                    <svg
                      className="w-24 h-24 mx-auto text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    No lessons added yet
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    Start by adding the first lesson for this chapter
                  </p>
                  <button
                    onClick={handleAddLesson}
                    className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90"
                  >
                    <span className="text-base">+</span>
                    <span>Add Lesson</span>
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 3xl:grid-cols-3   gap-4">
                  {lessonsToRender.map((lesson) => {
                    const commonDescription = getCommonDescription(lesson);
                    const hasDescriptionPreview = Boolean(commonDescription && commonDescription.trim());

                    const isAudioPlaying = playingAudioId === lesson.id;
                    const audioProgressValue = audioProgress[lesson.id] || 0;

                    // Get lesson name - use title field first, then fallback to shared description
                    const lessonName = lesson.title || commonDescription || lesson.lesson_name || 'Untitled Lesson';

                    // View-type-aware preview data
                    const viewType = lesson.view_type || 'normal_view';
                    const viewItems = Array.isArray(lesson.view_items) ? lesson.view_items : [];
                    const sortedViewItems = [...viewItems]
                      .filter((it) => isViewItemPreviewable(it))
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                    const viewItemsPeek = sortedViewItems.slice(0, VIEW_ITEMS_PEEK_LIMIT);
                    const viewItemsExtraCount = Math.max(sortedViewItems.length - viewItemsPeek.length, 0);
                    const hasViewItemsPreview = viewItemsPeek.length > 0;

                    // Mantra preview: show the translation (fallback to meaning) on the card.
                    const mantraTranslation = (lesson.translation || '').trim();
                    const mantraMeaning = (lesson.meaning || '').trim();
                    const mantraPeekText = mantraTranslation || mantraMeaning;
                    const hasMantraPreview = Boolean(mantraPeekText);

                    const contentId = lesson.id || lesson._id || lesson.content_id;
                    return (
                      <div
                        key={contentId}
                        onClick={(e) => {
                          // Only navigate if clicking directly on the card, not on buttons
                          if (e.target.closest('button')) {
                            return;
                          }
                          if (lessonId && contentId && moduleId && chapterId) {
                            const quizPath = `/module/${moduleId}/chapters/${chapterId}/lesson/${lessonId}/content/${contentId}/quiz`;
                            // console.log('Navigating to quiz:', quizPath, { lessonId, contentId, moduleId, chapterId });
                            navigate(quizPath, {
                              state: {
                                fromName: lessonName,
                                moduleName,
                                chapterName,
                                unitName,
                                contentName: lessonName,
                              },
                            });
                          } else {
                            // console.warn('Cannot navigate - missing params:', { lessonId, contentId, moduleId, chapterId });
                          }
                        }}
                        className="bg-white rounded-xl p-4 sm:p-5 transition-shadow relative overflow-hidden hover:shadow-lg cursor-pointer flex flex-col"
                        style={{ boxShadow: '0px 3px 8px 0px #93939340' }}
                      >
                        {/* Top Row: Lesson Name (Left) and Arrow (Right) */}
                        <div className="flex items-center justify-between pb-3">
                          <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate flex-1 pr-2">
                            {lessonName}
                          </h3>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Arrow Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const contentId = lesson.id || lesson._id || lesson.content_id;
                                if (lessonId && contentId && moduleId && chapterId) {
                                  const quizPath = `/module/${moduleId}/chapters/${chapterId}/lesson/${lessonId}/content/${contentId}/quiz`;
                                  // console.log('Arrow button - Navigating to quiz:', quizPath, { lessonId, contentId, moduleId, chapterId });
                                  navigate(quizPath, {
                                    state: {
                                      fromName: lessonName,
                                      moduleName,
                                      chapterName,
                                      unitName,
                                      contentName: lessonName,
                                    },
                                  });
                                } else {
                                  // console.warn('Arrow button - Cannot navigate - missing params:', { lessonId, contentId, moduleId, chapterId, lesson });
                                }
                              }}
                              className="cursor-pointer hover:text-gray-600 transition-colors flex-shrink-0"
                              title="Go to quiz"
                            >
                              <HiArrowLeft className="w-4 h-4 rotate-180 text-gray-400" />
                            </button>
                          </div>
                        </div>

                        {viewType === 'normal_view' && commonDescription && (
                          <div className="mb-3">
                            <ExpandableText text={commonDescription} />
                          </div>
                        )}

                        {viewType !== 'normal_view' && viewType !== 'mantra' && viewItemsPeek.length > 0 && (
                          <div className="mb-3 space-y-1.5">
                            {viewItemsPeek.map((item, idx) => {
                              const itemType = normalizeViewItemType(item.type);
                              return (
                                <div
                                  key={item._id || `${item.order}-${idx}`}
                                  className="text-xs sm:text-sm text-gray-500 flex gap-1.5 items-start"
                                >
                                  <span className="text-gray-400 flex-shrink-0">{idx + 1}.</span>
                                  <span
                                    className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5 ${
                                      itemType === 'custom'
                                        ? 'bg-amber-50 text-amber-700'
                                        : 'bg-slate-100 text-slate-600'
                                    }`}
                                  >
                                    {itemType === 'custom' ? 'Custom' : 'HTML'}
                                  </span>
                                  {itemType === 'custom' && item.image ? (
                                    <img
                                      src={item.image}
                                      alt=""
                                      className="w-8 h-8 rounded object-cover border border-gray-200 flex-shrink-0"
                                    />
                                  ) : null}
                                  <span className="line-clamp-1 overflow-hidden flex-1">
                                    {getViewItemPeekText(item)}
                                    {itemType === 'custom' && item.text_size
                                      ? ` · ${parseTextSizeFromApi(item.text_size)}px`
                                      : ''}
                                  </span>
                                </div>
                              );
                            })}
                            {viewItemsExtraCount > 0 && (
                              <p className="text-[11px] text-gray-400 italic">
                                +{viewItemsExtraCount} more
                              </p>
                            )}
                          </div>
                        )}

                        {viewType === 'mantra' && hasMantraPreview && (
                          <div className="mb-3">
                            <p className="text-xs sm:text-sm text-gray-500 line-clamp-2 overflow-hidden">
                              {mantraPeekText}
                            </p>
                          </div>
                        )}

                        {/* Second Row: Content Type Indicators (Image, Video, Audio) */}
                        {/* Media Preview Row */}
                        <div className="grid grid-cols-3 gap-3 mt-2">

                          {/* IMAGE */}
                          {lesson.image && (
                            <div className="flex flex-col items-start  text-left w-full ">
                              <img
                                src={lesson.image}
                                alt="Lesson Image"
                                className="w-full h-24 object-cover rounded-lg bg-gray-100"
                              />
                            </div>
                          )}

                          {/* VIDEO */}
                          {lesson.video && (
                            <div className="flex flex-col items-start text-left w-full">
                              <div className="relative w-full h-24 rounded-lg bg-black overflow-hidden">
                                <video
                                  src={lesson.video}
                                  className="w-full h-full object-cover"
                                  muted
                                />
                                <button
                                  onClick={(e) => handleVideoFullscreen(lesson.video, e)}
                                  className="absolute inset-0 flex items-center justify-center bg-black/30"
                                >
                                  <HiPlay className="w-8 h-8 text-white" />
                                </button>
                              </div>
                            </div>
                          )}

                          {/* AUDIO */}
                          {lesson.audio && (
                            <div className="flex flex-col items-start text-left w-full">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAudioPlay(lesson.id, lesson.audio);
                                }}
                                className="w-full h-24 rounded-lg flex items-center justify-center relative overflow-hidden"
                                style={{ backgroundColor: 'rgba(88, 50, 126, 0.08)' }}
                              >
                                {/* Minimal waveform audio icon */}
                                <div className="flex items-center justify-center gap-1.5">
                                  <div
                                    className="bg-brand rounded-sm"
                                    style={{
                                      width: '3px',
                                      height: isAudioPlaying ? '20px' : '12px',
                                      transition: 'height 0.3s ease',
                                      opacity: 0.6
                                    }}
                                  />
                                  <div
                                    className="bg-brand rounded-sm"
                                    style={{
                                      width: '3px',
                                      height: isAudioPlaying ? '28px' : '18px',
                                      transition: 'height 0.3s ease',
                                      opacity: 0.7
                                    }}
                                  />
                                  <div
                                    className="bg-brand rounded-sm"
                                    style={{
                                      width: '3px',
                                      height: isAudioPlaying ? '32px' : '24px',
                                      transition: 'height 0.3s ease',
                                      opacity: 0.8
                                    }}
                                  />
                                  <div
                                    className="bg-brand rounded-sm"
                                    style={{
                                      width: '3px',
                                      height: isAudioPlaying ? '28px' : '18px',
                                      transition: 'height 0.3s ease',
                                      opacity: 0.7
                                    }}
                                  />
                                  <div
                                    className="bg-brand rounded-sm"
                                    style={{
                                      width: '3px',
                                      height: isAudioPlaying ? '20px' : '12px',
                                      transition: 'height 0.3s ease',
                                      opacity: 0.6
                                    }}
                                  />
                                </div>
                                {/* Play/Pause overlay icon */}
                                {/* <div className="absolute inset-0 flex items-center justify-center">
          {isAudioPlaying ? (
            <HiPause className="w-6 h-6 text-brand opacity-60" />
          ) : (
            <HiPlay className="w-6 h-6 text-brand opacity-60 ml-0.5" />
          )}
        </div> */}
                              </button>
                            </div>
                          )}

                        </div>

                        {/* Footer: Points (bottom-left) + Actions (bottom-right) */}
                        <div className="mt-auto pt-3  flex items-center justify-between gap-3">
                          <div className="min-w-0 text-sm font-medium flex items-center gap-3 text-gray-600 flex-wrap">
                            <div className='px-2 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 text-gray-700 inline-flex items-center gap-1.5'>
                              {viewType === 'mantra' && (
                                <img src={mantraIcon} alt="Mantra" className="w-3.5 h-3.5 object-contain" />
                              )}
                              {getViewTypeLabel(viewType)}
                            </div>
                            <div className='px-2 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 text-gray-700'>
                              Points:{' '}
                              <span className="text-gray-700">{Number(lesson.points ?? 0)}</span>
                            </div>
                            {(() => {
                              const childCount =
                                (Number.isFinite(Number(lesson?.child_count)) ? Number(lesson.child_count) : null) ??
                                (Number.isFinite(Number(lesson?.children_count)) ? Number(lesson.children_count) : null) ??
                                (Array.isArray(lesson?.children) ? lesson.children.length : null) ??
                                (Array.isArray(lesson?.questions) ? lesson.questions.length : null) ??
                                null;

                              return (
                                <div className='px-2 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 text-gray-700'>
                                  Quiz:{' '}
                                  <span className="text-gray-700">{childCount ?? 0}</span>
                                </div>
                              );
                            })()}
                          </div>
                          <div className="flex justify-end gap-2 flex-shrink-0">
                            {(hasDescriptionPreview || hasViewItemsPreview || hasMantraPreview) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (viewType === 'normal_view') {
                                    openDescriptionPreview(commonDescription, lessonName);
                                  } else if (viewType === 'mantra') {
                                    const sections = [
                                      mantraTranslation && `<p><strong>Translation:</strong> ${escapeHtml(mantraTranslation)}</p>`,
                                      mantraMeaning && `<p><strong>Meaning:</strong> ${escapeHtml(mantraMeaning)}</p>`,
                                      (lesson.pronunciation || '').trim() && `<p><strong>Pronunciation:</strong> ${escapeHtml((lesson.pronunciation || '').trim())}</p>`,
                                      (lesson.quick_tip || '').trim() && `<p><strong>Quick Tip:</strong> ${escapeHtml((lesson.quick_tip || '').trim())}</p>`,
                                      (lesson.practice_guidance || '').trim() && `<p><strong>Practice Guidance:</strong> ${escapeHtml((lesson.practice_guidance || '').trim())}</p>`,
                                    ].filter(Boolean).join('');
                                    openDescriptionPreview(sections, `${lessonName} — Mantra`);
                                  } else {
                                    openViewItemsPreview(sortedViewItems, `${lessonName} — View Items`);
                                  }
                                }}
                                className="w-8 h-8 text-sm font-bold rounded-lg transition-colors hover:opacity-90 text-[#3B82F6] bg-[#EFF6FF] flex items-center justify-center"
                                title="Preview"
                              >
                                <HiEye className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(lesson); // Pass the entire lesson object
                              }}
                              className="w-8 h-8 text-sm font-bold rounded-lg transition-colors hover:opacity-90 text-brand bg-[#EFEBF3] flex items-center justify-center"
                              title="Edit"
                            >
                              <HiPencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(lesson); // Pass the entire lesson object
                              }}
                              className="w-8 h-8 font-bold text-sm rounded-lg transition-colors hover:opacity-90 text-[#F76464] bg-[#FFF0F0] flex items-center justify-center"
                              title="Delete"
                            >
                              <HiTrash className="w-4 h-4" />
                            </button>
                          </div>
                        </div>


                        {/* Description Row */}
                        {/* {description && (
                        <div className="mb-7">
                          <p className="text-xs sm:text-sm text-gray-500 line-clamp-2 overflow-hidden">
                            {description}
                          </p>
                        </div>
                      )} */}


                        {/* Round Image at Bottom Right (reuse for consistency) */}
                        <div className="absolute -mr-4 bottom-0 right-0 opacity-15 pointer-events-none">
                          <img
                            src={require('../../assets/round.png')}
                            alt="Round decoration"
                            className="w-28 h-28 sm:w-[150px] sm:h-[120px] object-contain"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {((totalPages > 1) || (totalPages === 1 && totalCount === limit)) && !isLoading && (
                  <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                        disabled={page === 1}
                        className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        aria-label="Previous page"
                      >
                        <HiChevronLeft className="w-4 h-4" />
                      </button>

                      <div className="flex items-center gap-1">
                        {getPaginationRange().map((pageNum, idx) =>
                          pageNum === 'dots' ? (
                            <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">
                              ...
                            </span>
                          ) : (
                            <button
                              key={pageNum}
                              onClick={() => setPage(pageNum)}
                              className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${pageNum === page
                                ? 'bg-brand border border-brand text-white shadow-sm hover:opacity-90'
                                : 'text-gray-900 hover:bg-gray-100'
                                }`}
                              aria-current={pageNum === page ? 'page' : undefined}
                            >
                              {pageNum}
                            </button>
                          )
                        )}
                      </div>

                      <button
                        onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={page === totalPages}
                        className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        aria-label="Next page"
                      >
                        <HiChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

              </>
            )}

          </div>
        </div>
      </div>

      {/* Add Lesson Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6 md:p-10 overflow-hidden">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full flex flex-col max-h-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Add New Lesson</h2>
              {/* <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowAddModal(false);
                  setOpenDropdownId(null);
                }}
              >
                ✕
              </button> */}
            </div>
            <div className="px-6 py-4 space-y-6 overflow-y-auto flex-1 no-scrollbar">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  View Type
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setViewTypeDropdownOpen((v) => !v)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent flex items-center justify-between text-left bg-white"
                  >
                    <span className="truncate">
                      {getViewTypeLabel(formData.view_type)}
                    </span>
                    <HiChevronDown className={`w-4 h-4 transition-transform duration-300 ease-in-out ${viewTypeDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {viewTypeDropdownOpen && (
                    <div className="absolute z-[60] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden py-1">
                      {VIEW_TYPE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            handleViewTypeChange(opt.value);
                            setViewTypeDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${formData.view_type === opt.value ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => {
                    setFormData({ ...formData, title: e.target.value });
                    if (formErrors.title) {
                      setFormErrors({ ...formErrors, title: '' });
                    }
                  }}
                  placeholder="Enter lesson title"
                  className={`w-full px-3 py-2 mb-0 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${formErrors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {formErrors.title && (
                  <p className="mt-1 text-xs  text-red-500">{formErrors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  placeholder="Optional subtitle under the title"
                  className="w-full px-3 py-2 mb-0 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>

              {formData.view_type !== 'normal_view' && formData.view_type !== 'mantra' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-700">
                      View Items
                    </label>
                    <button
                      type="button"
                      onClick={addViewItem}
                      className="text-xs font-semibold text-brand hover:opacity-80"
                    >
                      + Add Item
                    </button>
                  </div>
                  <DndContext
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={handleViewItemsReorder}
                  >
                    <SortableContext
                      items={formData.view_items.map((item) => item._id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {formData.view_items.map((item, index) => (
                          <SortableViewItem
                            key={item._id}
                            item={item}
                            index={index}
                            onChange={updateViewItem}
                            onTypeChange={handleViewItemTypeChange}
                            onRemove={removeViewItem}
                            onImageSelect={handleViewItemImageSelect}
                            onImageClear={handleViewItemImageClear}
                            onPreview={(i) =>
                              openViewItemsPreview(
                                [formData.view_items[i]],
                                `View Item ${i + 1} Preview`
                              )
                            }
                          />
                        ))}
                        {formData.view_items.length === 0 && (
                          <p className="text-xs text-gray-500 italic py-2">No items added. Click "+ Add Item" to create one.</p>
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {formErrors.view_items && (
                    <p className="mt-1 text-xs text-red-500">{formErrors.view_items}</p>
                  )}
                </div>
              )}

              {formData.view_type === 'mantra' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Translation
                    </label>
                    <textarea
                      rows={2}
                      value={formData.translation}
                      onChange={(e) => {
                        setFormData({ ...formData, translation: e.target.value });
                        if (formErrors.translation) setFormErrors({ ...formErrors, translation: '' });
                      }}
                      placeholder="Enter translation"
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none ${formErrors.translation ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.translation && (
                      <p className="mt-1 text-xs text-red-500">{formErrors.translation}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Meaning</label>
                    <textarea
                      rows={2}
                      value={formData.meaning}
                      onChange={(e) => setFormData({ ...formData, meaning: e.target.value })}
                      placeholder="Enter meaning"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Pronunciation</label>
                    <textarea
                      rows={2}
                      value={formData.pronunciation}
                      onChange={(e) => setFormData({ ...formData, pronunciation: e.target.value })}
                      placeholder="Enter pronunciation"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Quick Tip</label>
                    <textarea
                      rows={2}
                      value={formData.quick_tip}
                      onChange={(e) => setFormData({ ...formData, quick_tip: e.target.value })}
                      placeholder="Enter quick tip"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Practice Guidance</label>
                    <textarea
                      rows={2}
                      value={formData.practice_guidance}
                      onChange={(e) => setFormData({ ...formData, practice_guidance: e.target.value })}
                      placeholder="Enter practice guidance"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
                    />
                  </div>
                </div>
              )}

              {formData.view_type === 'normal_view' && (
                <div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-medium text-gray-700">
                        HTML View
                      </label>
                      <button
                        type="button"
                        onClick={() => openDescriptionPreview(formData.description, 'HTML View Preview')}
                        disabled={!formData.description?.trim()}
                        className="px-2.5 py-1 text-[11px] font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Preview
                      </button>
                    </div>
                    <textarea
                      value={formData.description}
                      onChange={(e) => {
                        setFormData({ ...formData, description: e.target.value });
                        if (formErrors.description) {
                          setFormErrors({ ...formErrors, description: '' });
                        }
                      }}
                      placeholder="Enter HTML content"
                      rows={5}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${formErrors.description ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {formErrors.description && (
                      <p className="mt-1 text-xs text-red-500">{formErrors.description}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Points */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Points
                </label>
                <input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={formData.points}
                  onChange={(e) => {
                    setFormData({ ...formData, points: e.target.value });
                    if (formErrors.points) {
                      setFormErrors({ ...formErrors, points: '' });
                    }
                  }}
                  placeholder="0"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${formErrors.points ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {formErrors.points && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.points}</p>
                )}
              </div>

              {formData.view_type !== 'mantra' && renderPreviewMediaFields('add')}

              {/* Audio — only shown for Normal View; other view types have their own UI */}
              {(!formData.view_type || formData.view_type === 'normal_view') && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Audio title
                    </label>
                    <input
                      type="text"
                      value={formData.audioText}
                      onChange={(e) => {
                        setFormData({ ...formData, audioText: e.target.value });
                        if (formErrors.audio) setFormErrors({ ...formErrors, audio: '' });
                      }}
                      placeholder="Title shown with this audio"
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${formErrors.audio ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                  </div>
                  {renderAudioHomeFields('add')}
                  {renderAudioFileField('add')}
                </div>
              )}

              {/* Order */}
              {/* <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Order
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.order}
                  onChange={(e) => {
                    setFormData({ ...formData, order: parseInt(e.target.value) || 1 });
                    if (formErrors.order) {
                      setFormErrors({ ...formErrors, order: '' });
                    }
                  }}
                  placeholder="1"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${formErrors.order ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {formErrors.order && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.order}</p>
                )}
              </div> */}
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                disabled={adding || uploading.image || uploading.video || uploading.audio || uploading.audioImage}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => handleFormSubmit(false)}
                disabled={adding || uploading.image || uploading.video || uploading.audio || uploading.audioImage}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {adding ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Adding...
                  </>
                ) : (
                  'Add Lesson '
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Lesson Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6 md:p-10 overflow-hidden">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full flex flex-col max-h-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Edit Lesson </h2>
              {/* <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowEditModal(false);
                  setOpenDropdownId(null);
                }}
              >
                ✕
              </button> */}
            </div>
            <div className="px-6 py-4 space-y-6 overflow-y-auto flex-1 no-scrollbar">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  View Type
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setViewTypeDropdownOpen((v) => !v)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent flex items-center justify-between text-left bg-white"
                  >
                    <span className="truncate">
                      {getViewTypeLabel(formData.view_type)}
                    </span>
                    <HiChevronDown className={`w-4 h-4 transition-transform duration-300 ease-in-out ${viewTypeDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {viewTypeDropdownOpen && (
                    <div className="absolute z-[60] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden py-1">
                      {VIEW_TYPE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            handleViewTypeChange(opt.value);
                            setViewTypeDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${formData.view_type === opt.value ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => {
                    setFormData({ ...formData, title: e.target.value });
                    if (formErrors.title) {
                      setFormErrors({ ...formErrors, title: '' });
                    }
                  }}
                  placeholder="Enter lesson title"
                  className={`w-full px-3 py-2 mb-0 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${formErrors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {formErrors.title && (
                  <p className="mt-1 text-xs  text-red-500">{formErrors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  placeholder="Optional subtitle under the title"
                  className="w-full px-3 py-2 mb-0 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>

              {formData.view_type !== 'normal_view' && formData.view_type !== 'mantra' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-700">
                      View Items
                    </label>
                    <button
                      type="button"
                      onClick={addViewItem}
                      className="text-xs font-semibold text-brand hover:opacity-80"
                    >
                      + Add Item
                    </button>
                  </div>
                  <DndContext
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={handleViewItemsReorder}
                  >
                    <SortableContext
                      items={formData.view_items.map((item) => item._id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {formData.view_items.map((item, index) => (
                          <SortableViewItem
                            key={item._id}
                            item={item}
                            index={index}
                            onChange={updateViewItem}
                            onTypeChange={handleViewItemTypeChange}
                            onRemove={removeViewItem}
                            onImageSelect={handleViewItemImageSelect}
                            onImageClear={handleViewItemImageClear}
                            onPreview={(i) =>
                              openViewItemsPreview(
                                [formData.view_items[i]],
                                `View Item ${i + 1} Preview`
                              )
                            }
                          />
                        ))}
                        {formData.view_items.length === 0 && (
                          <p className="text-xs text-gray-500 italic py-2">No items added. Click "+ Add Item" to create one.</p>
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {formErrors.view_items && (
                    <p className="mt-1 text-xs text-red-500">{formErrors.view_items}</p>
                  )}
                </div>
              )}

              {formData.view_type === 'mantra' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Translation
                    </label>
                    <textarea
                      rows={2}
                      value={formData.translation}
                      onChange={(e) => {
                        setFormData({ ...formData, translation: e.target.value });
                        if (formErrors.translation) setFormErrors({ ...formErrors, translation: '' });
                      }}
                      placeholder="Enter translation"
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none ${formErrors.translation ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.translation && (
                      <p className="mt-1 text-xs text-red-500">{formErrors.translation}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Meaning</label>
                    <textarea
                      rows={2}
                      value={formData.meaning}
                      onChange={(e) => setFormData({ ...formData, meaning: e.target.value })}
                      placeholder="Enter meaning"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Pronunciation</label>
                    <textarea
                      rows={2}
                      value={formData.pronunciation}
                      onChange={(e) => setFormData({ ...formData, pronunciation: e.target.value })}
                      placeholder="Enter pronunciation"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Quick Tip</label>
                    <textarea
                      rows={2}
                      value={formData.quick_tip}
                      onChange={(e) => setFormData({ ...formData, quick_tip: e.target.value })}
                      placeholder="Enter quick tip"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Practice Guidance</label>
                    <textarea
                      rows={2}
                      value={formData.practice_guidance}
                      onChange={(e) => setFormData({ ...formData, practice_guidance: e.target.value })}
                      placeholder="Enter practice guidance"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
                    />
                  </div>
                </div>
              )}

              {formData.view_type === 'normal_view' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-700">
                      HTML View
                    </label>
                    <button
                      type="button"
                      onClick={() => openDescriptionPreview(formData.description, 'HTML View Preview')}
                      disabled={!formData.description?.trim()}
                      className="px-2.5 py-1 text-[11px] font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Preview
                    </button>
                  </div>
                  <textarea
                    value={formData.description}
                    onChange={(e) => {
                      setFormData({ ...formData, description: e.target.value });
                      if (formErrors.description) {
                        setFormErrors({ ...formErrors, description: '' });
                      }
                    }}
                    placeholder="Enter HTML content"
                    rows={5}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${formErrors.description ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {formErrors.description && (
                    <p className="mt-1 text-xs text-red-500">{formErrors.description}</p>
                  )}
                </div>
              )}

              {/* Points */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Points
                </label>
                <input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={formData.points}
                  onChange={(e) => {
                    setFormData({ ...formData, points: e.target.value });
                    if (formErrors.points) {
                      setFormErrors({ ...formErrors, points: '' });
                    }
                  }}
                  placeholder="0"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${formErrors.points ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {formErrors.points && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.points}</p>
                )}
              </div>

              {formData.view_type !== 'mantra' && renderPreviewMediaFields('edit')}

              {/* Audio — only shown for Normal View; other view types have their own UI */}
              {(!formData.view_type || formData.view_type === 'normal_view') && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Audio title
                    </label>
                    <input
                      type="text"
                      value={formData.audioText}
                      onChange={(e) => {
                        setFormData({ ...formData, audioText: e.target.value });
                        if (formErrors.audio) setFormErrors({ ...formErrors, audio: '' });
                      }}
                      placeholder="Title shown with this audio"
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${formErrors.audio ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                  </div>
                  {renderAudioHomeFields('edit')}
                  {renderAudioFileField('edit')}
                </div>
              )}

              {/* Order */}
              {/* <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Order
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.order}
                  onChange={(e) => {
                    setFormData({ ...formData, order: parseInt(e.target.value) || 1 });
                    if (formErrors.order) {
                      setFormErrors({ ...formErrors, order: '' });
                    }
                  }}
                  placeholder="1"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${formErrors.order ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {formErrors.order && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.order}</p>
                )}
              </div> */}
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                disabled={editing || uploading.image || uploading.video || uploading.audio || uploading.audioImage}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => handleFormSubmit(true)}
                disabled={editing || uploading.image || uploading.video || uploading.audio || uploading.audioImage}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {editing ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  'Update Lesson '
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && lessonToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6 md:p-10">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">Delete Lesson </h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">
                Are you sure you want to delete this lesson?
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-[#F76464] rounded-lg  transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Video Modal */}
      {fullscreenVideo && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeFullscreenVideo}
        >
          <div className="relative w-full max-w-6xl h-full max-h-[90vh] flex items-center justify-center">
            <button
              onClick={closeFullscreenVideo}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
              title="Close"
            >
              <HiX className="w-6 h-6 text-white" />
            </button>
            <video
              src={fullscreenVideo}
              controls
              autoPlay
              className="w-full h-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Description Preview Modal */}
      {descriptionPreview.open && (
        <div
          className="fixed inset-0 bg-black/70 z-[55] flex items-center justify-center p-4"
          onClick={closeDescriptionPreview}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-[440px] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800 truncate pr-3">{descriptionPreview.title}</h3>
              <button
                onClick={closeDescriptionPreview}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Close preview"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>
            {descriptionPreview.viewItems && descriptionPreview.viewItems.length > 1 && (
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-center gap-1">
                  <button
                    type="button"
                    onClick={() => setDescriptionPreviewSlide(descriptionPreview.activeIndex - 1)}
                    disabled={descriptionPreview.activeIndex === 0}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Previous slide"
                  >
                    <HiChevronLeft className="w-4 h-4" />
                  </button>

                  <div
                    ref={previewPaginationRef}
                    onMouseDown={handlePreviewPaginationMouseDown}
                    className="flex items-center justify-start gap-1 min-w-[284px] max-w-[284px] overflow-x-auto scroll-smooth cursor-grab active:cursor-grabbing [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                  >
                    {descriptionPreview.viewItems.map((_, idx) => (
                      <button
                        key={`preview-page-${idx}`}
                        type="button"
                        data-preview-page-active={descriptionPreview.activeIndex === idx ? 'true' : undefined}
                        onClick={() => handlePreviewPaginationPageClick(idx)}
                        className={`inline-flex shrink-0 min-w-[32px] h-8 items-center justify-center px-2 text-sm font-semibold rounded-lg border transition-all duration-200 ${
                          descriptionPreview.activeIndex === idx
                            ? 'bg-brand border-brand text-white shadow-sm'
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100'
                        }`}
                        title={`View slide ${idx + 1}`}
                      >
                        {idx + 1}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setDescriptionPreviewSlide(descriptionPreview.activeIndex + 1)}
                    disabled={descriptionPreview.activeIndex === descriptionPreview.viewItems.length - 1}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Next slide"
                  >
                    <HiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            <div className="px-4 py-4 bg-gray-100 flex items-center justify-center">
              <div className="w-full max-w-[320px] h-[80vh] max-h-[760px] bg-black rounded-[28px] p-2 shadow-inner">
                <div className="w-full h-full bg-white rounded-[22px] overflow-hidden border border-gray-300">
                  <iframe
                    title="Description Mobile Preview"
                    srcDoc={descriptionPreviewDoc}
                    onLoad={handlePreviewIframeLoad}
                    className={`w-full h-full border-0 transition-opacity duration-300 ease-in-out ${
                      previewContentVisible ? 'opacity-100' : 'opacity-0'
                    }`}
                    sandbox="allow-same-origin allow-popups allow-forms"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload JSON Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Upload Lesson via JSON</h2>
            </div>
            <div className="px-6 py-6 space-y-4">
              <p className="text-sm text-gray-600">
                Upload a JSON file containing your learning lesson. Ensure the file follows the required format for successful processing.
              </p>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-700">Select JSON File</label>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand/10 file:text-brand hover:file:bg-brand/20 cursor-pointer"
                />
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button onClick={() => { setShowUploadModal(false); setSelectedFile(null); }} disabled={uploadingCsv} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleUploadCSV} disabled={uploadingCsv || !selectedFile} className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                {uploadingCsv ? (<><svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Uploading...</>) : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Result Modal */}
      {showUploadResultModal && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">JSON Upload Summary</h2>
              <button onClick={() => setShowUploadResultModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="border rounded-xl overflow-hidden">
                <button onClick={() => setOpenSection(openSection === 'success' ? null : 'success')} className="w-full flex items-center justify-between px-4 py-3 bg-green-50 hover:bg-green-100 transition">
                  <span className="font-semibold text-green-700">Successfully Added ({uploadResult.successful.length})</span>
                  <HiChevronDown className={`w-5 h-5 text-green-700 ${openSection === 'success' ? 'rotate-180' : ''}`} />
                </button>
                <div className={`px-4 bg-white overflow-hidden overflow-y-auto transition-all ${openSection === 'success' && uploadResult.successful.length > 0 ? 'max-h-40 opacity-100 py-3' : 'max-h-0 opacity-0 py-0'}`}>
                  <ul className="text-sm text-green-700 space-y-2">
                    {uploadResult.successful.map((item, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="font-semibold whitespace-nowrap">Row {item.row}:</span>
                        <span className="text-gray-700">
                          <div>module: {item.module}- {item.module_name}</div>
                          <div>Chapter: {item.chapter}- {item.chapter_name}</div>
                          <div>Unit: {item.lesson}- {item.lesson_name}</div>
                          <div>Lesson: {item.content}- {item.content_name}</div>
                          <div>Question: {item.question}- {item.question_text}</div>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="border rounded-xl overflow-hidden">
                <button onClick={() => setOpenSection(openSection === 'skipped' ? null : 'skipped')} className="w-full flex items-center justify-between px-4 py-3 bg-yellow-50 hover:bg-yellow-100 transition">
                  <span className="font-semibold text-yellow-700">Skipped Records ({uploadResult.skipped.length})</span>
                  <HiChevronDown className={`w-5 h-5 text-yellow-700 ${openSection === 'skipped' ? 'rotate-180' : ''}`} />
                </button>
                <div className={`px-4 bg-white overflow-hidden overflow-y-auto transition-all ${openSection === 'skipped' && uploadResult.skipped.length > 0 ? 'max-h-40 opacity-100 py-3' : 'max-h-0 opacity-0 py-0'}`}>
                  <ul className="text-sm text-yellow-700 space-y-2">
                    {uploadResult.skipped.map((item, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="font-semibold whitespace-nowrap">Row {item.row}:</span>
                        <span className="text-gray-700">
                          <div>module: {item.module}- {item.module_name}</div>
                          <div>Chapter: {item.chapter}- {item.chapter_name}</div>
                          <div>Unit: {item.lesson}- {item.lesson_name}</div>
                          <div>Lesson: {item.content}- {item.content_name}</div>
                          <div>Question: {item.question}- {item.reason}</div>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {uploadResult.error.length > 0 && (
                <div className="border border-red-200 rounded-xl overflow-hidden bg-red-50">
                  <button onClick={() => setOpenSection(openSection === 'error' ? null : 'error')} className="w-full flex items-center justify-between px-4 py-3 hover:bg-red-100 transition">
                    <span className="font-semibold text-red-700">Errors ({uploadResult.error.length})</span>
                    <HiChevronDown className={`w-5 h-5 text-red-700 ${openSection === 'error' ? 'rotate-180' : ''}`} />
                  </button>
                  <ul className={`px-4 text-sm text-red-700 space-y-2 overflow-hidden list-disc list-inside transition-all overflow-y-auto ${openSection === 'error' ? 'max-h-40 opacity-100 py-3' : 'max-h-0 opacity-0 py-0'}`}>
                    {uploadResult.error.map((item, i) => (
                      <li key={i}>Row {item.row} — {item.error || item.reason || item.message || 'Unknown server error'}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end">
              <button onClick={() => { setShowUploadResultModal(false); setOpenSection(null); }} className="px-5 py-2 text-sm font-semibold text-white bg-brand rounded-lg hover:opacity-90">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Reorder Modal */}
      {showReorderModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-hidden">
          <div className="bg-[#ffffff] w-full max-w-lg rounded-2xl p-6 md:ml-0 lg:ml-6 xl:ml-0 flex flex-col max-h-[90vh] overflow-hidden overflow-x-hidden">

            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-black/60 text-lg font-semibold">
                Reorder Lesson
              </h2>
              {/* <button
                onClick={() => setShowReorderModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button> */}
            </div>

            <p className="text-sm text-gray-400 mb-4 flex-shrink-0">
              Drag and drop lesson to reorder them. Click <b>Save Order</b> to apply.
            </p>

            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragStart={() => {

                document.body.style.overflowX = 'hidden';
              }}
              onDragEnd={(event) => {

                document.body.style.overflowX = '';

                const { active, over } = event;
                if (!over || active.id === over.id) return;

                setReorderLessonContents((prev) => {
                  const oldIndex = prev.findIndex(
                    (lc) => (lc.id || lc._id || lc.content_id) === active.id
                  );
                  const newIndex = prev.findIndex(
                    (lc) => (lc.id || lc._id || lc.content_id) === over.id
                  );
                  return arrayMove(prev, oldIndex, newIndex);
                });
              }}
              onDragCancel={() => {
                document.body.style.overflowX = '';
              }}
            >
              <SortableContext
                items={reorderLessonContents.map(
                  (lc) => lc.id || lc._id || lc.content_id
                )}
                strategy={verticalListSortingStrategy}
              >

                <div className="space-y-3 overflow-y-auto overflow-x-hidden pr-2 flex-1 min-h-0" style={{ maxHeight: '420px' }}>
                  {reorderLessonContents.map((lessonContent, index) => (
                    <SortableLessonContentItem
                      key={lessonContent.id || lessonContent._id || lessonContent.content_id}
                      lessonContent={lessonContent}
                      index={index}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>


            <div className="flex justify-end gap-3 mt-6 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowReorderModal(false)}
                disabled={reorderSaveLoading}
                className="px-4 py-1 rounded-lg border border-gray-600 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveLessonContentOrder}
                disabled={reorderSaveLoading}
                className="px-4 py-2 rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {reorderSaveLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Order'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonContent;



